import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import { CompaniesService } from '../../components/alldocuments/alldocuments-companies/companies.service';
import { Company } from '../../components/alldocuments/alldocuments-companies/alldocuments-companies.model';
import { AiInvoiceVoiceService } from '../../services/ai-invoice-voice.service';
import { InvoiceV2Service } from '../../services/invoice-v2.service';
import { baseUrl } from '../service/global.service';
import { HttpHelpService } from '../service/http-help.service';
import {
  AiInvoiceConfirmResponseDto,
  AiInvoiceUiBootstrapDto,
  AiServerChatMessageDto,
  AiSessionChatMetaDto,
  AiSessionListItemDto,
  AiSessionMode,
  AiSuggestedCustomerDetailsDto,
  AiSuggestedProductDetailsDto,
  AiVoiceOptionDto,
  AiVoiceResponseDto,
  AiVoiceStatus,
  PostAiResolveEntityDto,
  ReportDataDto,
  StartAiVoiceSessionDto,
  StartAiVoiceSessionResponseDto,
  VoiceChatMessage,
} from '../../models/ai-invoice-voice.model';
import { Customer } from '../../components/models/customer.model';

const SESSION_STORAGE_KEY = 'aiVoiceInvoice.session.v2';

/** Invoice document-type quick-start chips (codes shown before first message). */
const DOC_TYPE_SUGGESTION_CODES = [305, 400, 320, 0, 332] as const;

const REPORT_SUGGESTED_PROMPTS: Record<string, string[]> = {
  ar: [
    'أعطني إجمالي المبيعات هذا الشهر',
    'من هم أعلى 5 عملاء من حيث المبيعات؟',
    'ما هي أكثر المنتجات مبيعاً؟',
    'قارن مبيعات هذا الشهر بالشهر الماضي',
    'كم عدد الفواتير هذا الشهر؟',
    'ما إجمالي المدفوعات النقدية هذا الشهر؟',
    'أعطني قائمة الزبائن مع إجمالي مشترياتهم',
    'ما إجمالي الإيرادات هذا العام؟',
  ],
  en: [
    'Show me total sales this month',
    'Who are the top 5 customers by sales?',
    'What are the best-selling products?',
    "Compare this month's sales to last month",
    'How many invoices were issued this month?',
    'What is the total cash received this month?',
    'List customers with their total purchases',
    'What is the total revenue this year?',
  ],
  he: [
    'הצג לי את סך המכירות החודש',
    'מי הם 5 הלקוחות המובילים לפי מכירות?',
    'מהם המוצרים הנמכרים ביותר?',
    'השווה בין מכירות החודש לחודש שעבר',
    'כמה חשבוניות הוצאו החודש?',
    'מה סך התשלומים במזומן החודש?',
    'רשום לקוחות עם סך הרכישות שלהם',
    'מה סך ההכנסות השנה?',
  ],
};

/** Web Speech API — typings not always present in TS `lib.dom`. */
type WebSpeechRecognition = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((ev: Event) => void) | null;
  onerror: ((ev: Event) => void) | null;
  onend: (() => void) | null;
};

interface PersistedSession {
  sessionId: string;
  companyId: number;
}

interface InvoiceTypeOption {
  code: number;
  labelAr: string;
  labelEn: string;
  labelHe: string;
  has_items?: boolean;
  has_payments?: boolean;
}

interface UnderstoodSummaryRow {
  id: string;
  labelKey: string;
  value: string;
  status: 'ok' | 'warn' | 'missing';
  icon: string;
}

type QuickReplyVariant = 'primary' | 'secondary';

interface VoiceQuickReplyButton {
  id: string;
  labelKey: string;
  sendText?: string;
  variant: QuickReplyVariant;
  icon?: string;
  action?: 'open_customer_modal' | 'open_product_modal';
}

@Component({
  selector: 'app-ai-voice-invoice',
  templateUrl: './ai-voice-invoice.component.html',
  styleUrls: ['./ai-voice-invoice.component.scss'],
})
export class AiVoiceInvoiceComponent implements OnInit, OnDestroy {
  @ViewChild('chatScroll') chatScroll?: ElementRef<HTMLElement>;
  @ViewChild('imageInput') imageInput?: ElementRef<HTMLInputElement>;
  @ViewChild('composerTextarea') composerTextarea?: ElementRef<HTMLTextAreaElement>;

  curentLang = localStorage.getItem('curentLang') || 'ar';

  /** From GET /ui/bootstrap (preferred). */
  bootstrap: AiInvoiceUiBootstrapDto | null = null;
  bootstrapLoaded = false;
  useLegacyCompanies = false;

  companies: Company[] = [];
  selectedCompany: Company | null = null;
  sessionMode: AiSessionMode = 'invoice';
  /** True until the user sends their first message in a report session. */
  showReportSuggestions = false;
  invoiceType = 305;
  documentLanguage = localStorage.getItem('curentLang') || 'ar';

  invoiceTypeOptions: InvoiceTypeOption[] = [];

  sessionTtlMinutes = 10;
  private get sessionTtlMs(): number {
    return this.sessionTtlMinutes * 60 * 1000;
  }

  sessionList: AiSessionListItemDto[] = [];
  sessionsTotal = 0;
  activeSidebarSessionId: string | null = null;

  sessionId: string | null = null;
  /** From GET .../chat — if false, chat is read-only for sending. */
  canContinueChat = true;
  sessionDeadline: number | null = null;
  remainingLabel = '';
  private timerHandle: ReturnType<typeof setInterval> | null = null;

  isRecording = false;
  /** Text or selection request in flight. */
  isAiProcessing = false;
  isConfirming = false;
  isUploadingImage = false;
  /** Image file waiting to be uploaded once the onboarding completes and the session starts. */
  private pendingImageFile: File | null = null;
  micError: string | null = null;
  draftText = '';

  /** Browser STT capability probed once on init. */
  supportsBrowserStt = false;

  lastResponse: AiVoiceResponseDto | null = null;
  confirmResult: AiInvoiceConfirmResponseDto | null = null;

  /** From GET .../chat when the session is already linked to an invoice — hide manual confirm. */
  sessionLinkedInvoiceId: number | null = null;

  /* ----------------------------- Real-time invoice preview ----------------------------- */
  /** Currently previewed invoice (driven by AI replies + session). */
  previewInvoiceId: number | null = null;
  /** Sanitized HTML snapshot for the preview iframe. */
  previewSafeHtml: SafeHtml | null = null;
  /** True while the `/invoices-v2/:id/html` request is in flight. */
  previewLoading = false;
  /** Last preview load error message, if any. */
  previewError: string | null = null;
  /** Wallclock of last successful preview render (for "updated x ago" hint). */
  previewLastUpdatedAt: number | null = null;

  /** Loaded with preview HTML — drives “send to tax authority” checkbox (PUT allocation-number). */
  previewInvoiceMetaLoaded = false;
  /** False when GET invoice meta failed or returned unusable payload. */
  previewInvoiceMetaAvailable = false;
  /** 0 = draft/unconfirmed, 1 = confirmed — PUT allocation-number applies only when 0. */
  previewInvoiceConfirm: number | null = null;
  previewAllocationChecked = false;
  previewAllocationSaving = false;

  private previewSubscription?: Subscription;

  /** Latest draft snapshot from GET .../chat — powers the «understood» summary card. */
  sessionDraftSnapshot: Record<string, unknown> | null = null;

  chatMessages: VoiceChatMessage[] = [];

  /** Pending product/customer ids for the active `multi_select` step (cleared on each new assistant reply). */
  private readonly pendingMultiOptionIds = new Set<number>();

  /** Cached active customers for the selected company (AIBOT-3 local match fallback). */
  private companyCustomersCache: any[] = [];
  private companyCustomersCacheCompanyId: number | null = null;
  /** Consecutive identical assistant prompts — after 2, offer recovery actions. */
  private lastAssistantPromptNorm = '';
  private identicalAssistantPromptCount = 0;

  /** CRM «add customer» modal — opened when assistant sets `open_modal_create_customer`. */
  showAddCustomerModal = false;
  /** Catalog «add product» modal — opened when assistant sets `open_modal_create_product`. */
  showAddProductModal = false;
  pendingSuggestedCustomerName: string | null = null;
  pendingSuggestedProductName: string | null = null;
  /** Full customer details from image extraction — prefills all add-customer form fields. */
  pendingSuggestedCustomerDetails: AiSuggestedCustomerDetailsDto | null = null;
  /** Full product details from image extraction — prefills add-product form fields. */
  pendingSuggestedProductDetails: AiSuggestedProductDetailsDto | null = null;

  /* ----------------------------- Pre-session onboarding chat flow ----------------------------- */
  /** Current onboarding step — 'idle' when session already exists or not yet started. */
  onboardingStep: 'idle' | 'company' = 'idle';
  /** User's original message captured before session started — sent once setup completes. */
  private pendingDraft = '';

  /** Browser speech-to-text (Chrome/Edge/Safari). */
  private speechRecognition: WebSpeechRecognition | null = null;
  private sttFinalAccum = '';
  private sttErrored = false;
  private sttIgnoreOnEnd = false;

  constructor(
    private companiesService: CompaniesService,
    private httpHelp: HttpHelpService,
    private aiInvoiceVoice: AiInvoiceVoiceService,
    private translate: TranslateService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private invoiceV2: InvoiceV2Service,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    this.supportsBrowserStt = !!this.getSpeechRecognitionCtor();
    forkJoin({
      bootstrap: this.aiInvoiceVoice.getBootstrap().pipe(catchError(() => of(null))),
      sessions: this.aiInvoiceVoice.getSessions({ limit: 20 }).pipe(
        catchError(() => of({ data: [], total: 0, limit: 20, offset: 0 })),
      ),
      invoiceTypesActive: this.httpHelp.GetInvoiceTypeList().pipe(catchError(() => of(null))),
      /* Active companies only — bootstrap may include archived without company_status. */
      activeCompanies: this.companiesService.getCompanies().pipe(catchError(() => of([]))),
      archivedCompanies: this.httpHelp.getArchivedCompanies().pipe(catchError(() => of([]))),
    }).subscribe({
      next: ({ bootstrap, sessions, invoiceTypesActive, activeCompanies, archivedCompanies }) => {
        /* Invoice types always come from the active-list API — apply first. */
        this.applyInvoiceTypesFromActiveList(invoiceTypesActive);

        if (bootstrap) {
          this.applyBootstrap(bootstrap);
        } else {
          this.useLegacyCompanies = true;
        }
        this.applyActiveCompanies(activeCompanies, archivedCompanies);
        this.ensureInvoiceTypeSelectionValid();
        this.bootstrapLoaded = true;
        this.sessionList = sessions.data || [];
        this.sessionsTotal = sessions.total ?? 0;
        this.tryRestorePersistedSession();
        this.cdr.markForCheck();
      },
      error: () => {
        this.loadCompaniesFallback();
        this.bootstrapLoaded = true;
        this.cdr.markForCheck();
      },
    });
  }

  ngOnDestroy(): void {
    this.clearTimer();
    this.abortSpeechRecognitionSilently();
    this.previewSubscription?.unsubscribe();
  }

  /** Exclude archived companies from onboarding / session setup (matches header selector). */
  private isActiveCompanyStatus(status: unknown): boolean {
    return status !== 'ARCHIVED';
  }

  /** Same source as header company picker: GET company/getAllActiveCompanyByMerchant. */
  private applyActiveCompanies(companies: Company[], archivedResponse?: unknown): void {
    const archivedIds = this.archivedCompanyIds(archivedResponse);
    this.companies = (companies || []).filter((c) => {
      const id = Number(c.value ?? c.code);
      if (archivedIds.size > 0 && Number.isFinite(id) && archivedIds.has(id)) {
        return false;
      }
      return this.isActiveCompanyStatus(
        (c.fullData as { company_status?: string } | undefined)?.company_status,
      );
    });
  }

  private archivedCompanyIds(response: unknown): Set<number> {
    const rows = Array.isArray(response)
      ? response
      : response &&
          typeof response === 'object' &&
          Array.isArray((response as { data?: unknown[] }).data)
        ? (response as { data: unknown[] }).data
        : [];
    const ids = new Set<number>();
    for (const row of rows) {
      if (!row || typeof row !== 'object') continue;
      const id = Number((row as { company_id?: number }).company_id);
      if (Number.isFinite(id)) ids.add(id);
    }
    return ids;
  }

  private applyBootstrap(b: AiInvoiceUiBootstrapDto): void {
    this.bootstrap = b;
    this.sessionTtlMinutes = b.session_ttl_minutes ?? 10;

    if (b.defaults?.invoice_type != null) {
      this.invoiceType = Number(b.defaults.invoice_type);
    }
  }

  /**
   * Primary source for invoice types: `GET invoice-type-list/active` via `HttpHelpService`.
   */
  private applyInvoiceTypesFromActiveList(response: unknown): void {
    let typesData: unknown[] = [];
    if (Array.isArray(response)) {
      typesData = response;
    } else if (
      response &&
      typeof response === 'object' &&
      Array.isArray((response as { data?: unknown[] }).data)
    ) {
      typesData = (response as { data: unknown[] }).data;
    }
    if (!typesData.length) return;

    const CANCELLED_RECEIPT = 430;
    const rows = typesData as Array<Record<string, unknown>>;
    const mapped = rows
      .filter((t) => {
        const code = Number(t['invoice_type_code']);
        if (code === CANCELLED_RECEIPT) return false;
        const st = t['status'];
        if (st != null && Number(st) !== 1) return false;
        return Number.isFinite(code);
      })
      .map<InvoiceTypeOption>((t) => {
        const code = Number(t['invoice_type_code']);
        return {
          code,
          labelAr: String(t['invoice_type_name_ar'] ?? code),
          labelEn: String(t['invoice_type_name_en'] ?? code),
          labelHe: String(t['invoice_type_name_he'] ?? code),
          has_items: t['has_items'] === true || t['has_items'] === 1,
          has_payments: t['has_payments'] === true || t['has_payments'] === 1,
        };
      });

    if (!mapped.length) return;
    this.invoiceTypeOptions = mapped;
  }

  private ensureInvoiceTypeSelectionValid(): void {
    const codes = new Set(this.invoiceTypeOptions.map((o) => o.code));
    if (!codes.has(this.invoiceType) && this.invoiceTypeOptions.length) {
      this.invoiceType = this.invoiceTypeOptions[0].code;
    }
  }

  private loadCompaniesFallback(): void {
    this.useLegacyCompanies = true;
    forkJoin({
      companies: this.companiesService.getCompanies().pipe(catchError(() => of([]))),
      archived: this.httpHelp.getArchivedCompanies().pipe(catchError(() => of([]))),
      types: this.httpHelp.GetInvoiceTypeList().pipe(catchError(() => of(null))),
    }).subscribe(({ companies, archived, types }) => {
      this.applyActiveCompanies(companies || [], archived);
      this.applyInvoiceTypesFromActiveList(types);
      this.ensureInvoiceTypeSelectionValid();
      this.cdr.markForCheck();
    });
  }

  private tryRestorePersistedSession(): void {
    try {
      const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (!raw) {
        return;
      }
      const p = JSON.parse(raw) as PersistedSession;
      if (!p.sessionId) {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
        return;
      }
      this.openSessionFromServer(p.sessionId);
    } catch {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
  }

  /** User selected company from bootstrap — start session automatically. */
  onCompanySelected(): void {
    if (!this.selectedCompany?.value || this.sessionId) return;
    this.startSession();
  }

  get chatDir(): 'rtl' | 'ltr' {
    return this.curentLang === 'ar' || this.curentLang === 'he' ? 'rtl' : 'ltr';
  }

  get reportSuggestionChips(): string[] {
    const lang = this.curentLang === 'he' ? 'he' : this.curentLang === 'en' ? 'en' : 'ar';
    return (REPORT_SUGGESTED_PROMPTS[lang] ?? REPORT_SUGGESTED_PROMPTS['ar']).slice(0, 4);
  }

  /** Clickable example cards shown in the empty chat state. */
  get exampleCards(): Array<{ titleKey: string; textKey: string; icon: string; tone: string }> {
    return [
      {
        titleKey: 'aiVoiceInvoice.exampleCardTitle1',
        textKey: 'aiVoiceInvoice.exampleCardText1',
        icon: 'fa-money',
        tone: 'mint',
      },
      {
        titleKey: 'aiVoiceInvoice.exampleCardTitle2',
        textKey: 'aiVoiceInvoice.exampleCardText2',
        icon: 'fa-tag',
        tone: 'sky',
      },
      {
        titleKey: 'aiVoiceInvoice.exampleCardTitle3',
        textKey: 'aiVoiceInvoice.exampleCardText3',
        icon: 'fa-file-text-o',
        tone: 'peach',
      },
    ];
  }

  get guideSteps(): Array<{ icon: string; titleKey: string; descKey: string }> {
    return [
      {
        icon: 'fa-comments-o',
        titleKey: 'aiVoiceInvoice.guideStep1Title',
        descKey: 'aiVoiceInvoice.guideStep1Desc',
      },
      {
        icon: 'fa-magic',
        titleKey: 'aiVoiceInvoice.guideStep2Title',
        descKey: 'aiVoiceInvoice.guideStep2Desc',
      },
      {
        icon: 'fa-question-circle',
        titleKey: 'aiVoiceInvoice.guideStep3Title',
        descKey: 'aiVoiceInvoice.guideStep3Desc',
      },
    ];
  }

  get showWelcomePanel(): boolean {
    return !this.chatMessages.length && !this.isAiProcessing && !this.confirmResult;
  }

  get merchantInitials(): string {
    const name =
      (this.selectedCompany?.label || this.selectedCompany?.name || '').trim() ||
      'U';
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }

  focusComposerForEdit(): void {
    const el = this.composerTextarea?.nativeElement;
    if (!el) return;
    el.focus();
    const len = el.value.length;
    el.setSelectionRange(len, len);
    this.scrollChatToBottom();
  }

  /** Document-type quick-start chips (invoice mode, empty chat). */
  get docSuggestionChips(): Array<{ code: number; label: string; template: string; icon: string }> {
    const lang = this.curentLang === 'he' ? 'he' : this.curentLang === 'en' ? 'en' : 'ar';
    const iconByCode: Record<number, string> = {
      305: 'fa-file-text-o',
      400: 'fa-money',
      320: 'fa-files-o',
      0: 'fa-tag',
      332: 'fa-exclamation-circle',
    };
    const templateKeyByCode: Record<number, string> = {
      305: 'aiVoiceInvoice.docSuggestionTemplate305',
      400: 'aiVoiceInvoice.docSuggestionTemplate400',
      320: 'aiVoiceInvoice.docSuggestionTemplate320',
      0: 'aiVoiceInvoice.docSuggestionTemplate0',
      332: 'aiVoiceInvoice.docSuggestionTemplate332',
    };
    return DOC_TYPE_SUGGESTION_CODES.map((code) => {
      const opt = this.invoiceTypeOptions.find((o) => o.code === code);
      const label = opt
        ? lang === 'ar'
          ? opt.labelAr
          : lang === 'he'
            ? opt.labelHe
            : opt.labelEn
        : String(code);
      return {
        code,
        label,
        template: this.translate.instant(templateKeyByCode[code] ?? 'aiVoiceInvoice.docSuggestionTemplate305'),
        icon: iconByCode[code] ?? 'fa-file-text-o',
      };
    });
  }

  get showDocSuggestions(): boolean {
    return (
      !this.chatMessages.length &&
      !this.isAiProcessing &&
      !this.confirmResult &&
      this.sessionMode === 'invoice'
    );
  }

  /** Fill the composer with an example or suggestion and focus it. */
  clickExample(text: string): void {
    if (this.isAiProcessing || this.isConfirming) return;
    if (this.sessionIsReadOnly) return;
    const trimmed = (text || '').trim();
    if (!trimmed) return;
    this.draftText = trimmed;
    this.cdr.markForCheck();
    setTimeout(() => {
      const el = this.composerTextarea?.nativeElement;
      if (el) {
        el.focus();
        const len = el.value.length;
        el.setSelectionRange(len, len);
      }
    }, 0);
  }

  clickExampleByKey(i18nKey: string): void {
    this.clickExample(this.translate.instant(i18nKey));
  }

  applyDocSuggestion(template: string): void {
    this.clickExample(template);
  }

  get typeLabel(): string {
    const opt = this.invoiceTypeOptions.find((o) => o.code === this.invoiceType);
    if (!opt) return String(this.invoiceType);
    if (this.curentLang === 'ar') return opt.labelAr;
    if (this.curentLang === 'he') return opt.labelHe;
    return opt.labelEn;
  }

  /** Can send text/selection/confirm (Redis alive + local timer). */
  get canSendMessages(): boolean {
    return (
      !!this.sessionId &&
      this.canContinueChat &&
      !!this.sessionDeadline &&
      this.sessionDeadline > Date.now()
    );
  }

  get hasSession(): boolean {
    return !!this.sessionId;
  }

  get sessionIsReadOnly(): boolean {
    return this.hasSession && !this.canSendMessages;
  }

  refreshSessions(): void {
    this.aiInvoiceVoice.getSessions({ limit: 20 }).subscribe({
      next: (r) => {
        this.sessionList = r.data || [];
        this.sessionsTotal = r.total ?? 0;
        this.cdr.markForCheck();
      },
      error: () => {},
    });
  }

  selectSessionFromSidebar(item: AiSessionListItemDto): void {
    if (item.sessionId === this.sessionId) return;
    this.openSessionFromServer(item.sessionId);
  }

  private openSessionFromServer(sessionId: string): void {
    this.onboardingStep = 'idle';
    this.pendingDraft = '';
    this.pendingImageFile = null;
    this.spinner.show();
    this.aiInvoiceVoice.getSessionChat(sessionId).subscribe({
      next: (resp) => {
        this.spinner.hide();
        this.confirmResult = null;
        this.sessionId = sessionId;
        this.activeSidebarSessionId = sessionId;
        this.canContinueChat = resp.session.canContinueChat !== false;
        const meta = resp.session;
        this.sessionLinkedInvoiceId = this.parseSessionInvoiceId(meta.invoice_id);
        this.sessionDraftSnapshot = (meta.draft_snapshot as Record<string, unknown>) ?? null;
        const lastInvoiceFromMessages = this.findLatestInvoiceIdInRows(resp.messages || []);
        const initialPreviewId = this.sessionLinkedInvoiceId ?? lastInvoiceFromMessages;
        this.setPreviewInvoiceId(initialPreviewId);
        if (meta.invoice_type != null) {
          this.invoiceType = Number(meta.invoice_type);
        }
        if (meta.document_language) {
          this.documentLanguage = meta.document_language;
        }
        const ttlMin = Number(resp.session.session_ttl_minutes ?? this.sessionTtlMinutes);
        this.chatMessages = this.mapServerMessagesToChat(resp.messages || []);
        this.applyLastResponseFromServerMessages(resp.messages || []);

        if (this.canContinueChat) {
          this.sessionDeadline = Date.now() + ttlMin * 60 * 1000;
          this.persistSession();
          this.startTimer();
        } else {
          this.clearTimer();
          this.sessionDeadline = null;
        }

        const companyId = this.inferCompanyIdFromSession(resp.session);
        if (companyId) {
          this.selectedCompany =
            this.companies.find((c) => Number(c.value) === companyId) || this.selectedCompany;
        }
        if (this.canContinueChat) {
          const cid = companyId != null ? companyId : Number(this.selectedCompany?.value) || 0;
          this.persistSession(cid || undefined);
        }
        this.scrollChatToBottom();
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.spinner.hide();
        if (err.status === 404) {
          this.toastr.error(this.translate.instant('aiVoiceInvoice.sessionNotFound'));
          sessionStorage.removeItem(SESSION_STORAGE_KEY);
        } else {
          this.toastr.error(this.httpErrorMessage(err, 'aiVoiceInvoice.chatLoadError'));
        }
        this.cdr.markForCheck();
      },
    });
  }

  private inferCompanyIdFromSession(session: AiSessionChatMetaDto): number | null {
    const raw = session.companyId ?? (session as { company_id?: number }).company_id;
    return raw != null ? Number(raw) : null;
  }

  private parseSessionInvoiceId(value: unknown): number | null {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  /** Invoice already created (in-chat issuance or session linked) — hide POST /confirm. */
  private shouldHideManualConfirm(): boolean {
    if (this.sessionLinkedInvoiceId != null) return true;
    const ir = this.lastResponse?.invoice_result;
    if (ir && !ir.error && ir.invoice_id != null) return true;
    return false;
  }

  private mapServerMessagesToChat(rows: AiServerChatMessageDto[]): VoiceChatMessage[] {
    const sorted = [...rows].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
    return sorted.map((m) => this.mapOneServerMessage(m));
  }

  private mapOneServerMessage(m: AiServerChatMessageDto): VoiceChatMessage {
    const ts = new Date(m.createdAt).getTime();
    const id = String(m.id);
    if (m.role === 'merchant') {
      const content = (m.content ?? '').trim();
      const sid = m.selected_option_id;
      const listOnly = !content && sid != null;
      const text = listOnly
        ? this.translate.instant('aiVoiceInvoice.merchantListSelectionOnly', { id: sid })
        : content;
      return {
        id,
        role: 'merchant',
        turnId: id,
        createdAt: ts,
        text,
        merchantListSelectionOnly: listOnly || undefined,
      };
    }
    const meta = m.metadata as Partial<AiVoiceResponseDto> | undefined;
    const voiceResponse = meta
      ? {
          status: meta.status || 'error',
          message: meta.message || m.content,
          missing: meta.missing,
          options: meta.options,
          multi_select: meta.multi_select,
          confidence: meta.confidence,
          resolved_customer_id: meta.resolved_customer_id,
          resolved_customer_name: meta.resolved_customer_name,
          invoice_result: meta.invoice_result,
          can_confirm: meta.can_confirm,
          can_save_draft: meta.can_save_draft,
          open_modal_create_customer: meta.open_modal_create_customer,
          open_modal_create_product: meta.open_modal_create_product,
          suggested_customer_name: meta.suggested_customer_name,
          suggested_product_name: meta.suggested_product_name,
          report_html: meta.report_html,
          report_data: meta.report_data,
        }
      : {
          status: 'error' as AiVoiceStatus,
          message: m.content,
        };
    const reportHtml = meta?.report_html;
    return {
      id,
      role: 'system',
      turnId: id,
      createdAt: ts,
      text: m.content,
      voiceResponse,
      reportSafeHtml: reportHtml ? this.sanitizer.bypassSecurityTrustHtml(reportHtml) : undefined,
    };
  }

  private applyLastResponseFromServerMessages(rows: AiServerChatMessageDto[]): void {
    const systems = [...rows].filter((r) => r.role === 'system');
    const last = systems[systems.length - 1];
    if (!last?.metadata) {
      this.lastResponse = null;
      return;
    }
    const meta = last.metadata as AiVoiceResponseDto;
    if (meta && meta.status) {
      this.lastResponse = meta;
    } else {
      this.lastResponse = null;
    }
  }

  /** Mirror optional session metadata returned by POST /session/start. */
  private applyStartSessionResponse(res: StartAiVoiceSessionResponseDto): void {
    if (res.session_mode) {
      this.sessionMode = res.session_mode;
    }
    const typeCode = res.invoice_type_profile?.invoice_type_code;
    if (typeCode != null && Number.isFinite(Number(typeCode))) {
      this.invoiceType = Number(typeCode);
    }
  }

  private buildStartSessionBody(companyId: number): StartAiVoiceSessionDto {
    const body: StartAiVoiceSessionDto = {
      company_id: companyId,
      session_mode: this.sessionMode,
      document_language: this.documentLanguage,
    };
    if (this.sessionMode === 'invoice') {
      body.invoice_type = this.invoiceType;
    }
    return body;
  }

  startSession(): void {
    if (!this.selectedCompany?.value) {
      this.toastr.warning(this.translate.instant('aiVoiceInvoice.selectCompany'));
      return;
    }
    const companyId = Number(this.selectedCompany.value);
    this.spinner.show();
    this.aiInvoiceVoice
      .startSession(this.buildStartSessionBody(companyId))
      .subscribe({
        next: (res) => {
          this.spinner.hide();
          this.applyStartSessionResponse(res);
          this.confirmResult = null;
          this.lastResponse = null;
          this.sessionLinkedInvoiceId = null;
          this.setPreviewInvoiceId(null);
          this.chatMessages = [];
          this.sessionId = res.sessionId;
          this.activeSidebarSessionId = res.sessionId;
          this.canContinueChat = true;
          this.sessionDeadline = Date.now() + this.sessionTtlMs;
          this.persistSession(companyId);
          this.startTimer();
          this.toastr.success(this.translate.instant('aiVoiceInvoice.sessionStarted'));
          this.refreshSessions();
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.spinner.hide();
          this.toastr.error(this.httpErrorMessage(err, 'aiVoiceInvoice.startSessionError'));
          this.cdr.markForCheck();
        },
      });
  }

  private persistSession(companyId?: number): void {
    if (!this.sessionId) return;
    const cid = companyId ?? (Number(this.selectedCompany?.value) || 0);
    const payload: PersistedSession = { sessionId: this.sessionId, companyId: cid };
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(payload));
  }

  private clearPersistedSession(): void {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  }

  /** Header: new chat / new session — clears current UI state and returns to setup. */
  startNewChat(): void {
    if (this.isAiProcessing || this.isConfirming) return;
    this.abortSpeechRecognitionSilently();
    const hadActivity = this.hasSession || this.confirmResult != null || this.chatMessages.length > 0;
    this.endSession();
    this.draftText = '';
    this.micError = null;
    this.refreshSessions();
    if (hadActivity) {
      this.toastr.info(this.translate.instant('aiVoiceInvoice.newChatReady'));
    }
    this.cdr.markForCheck();
  }

  endSession(): void {
    this.clearTimer();
    this.sessionId = null;
    this.activeSidebarSessionId = null;
    this.sessionDeadline = null;
    this.remainingLabel = '';
    this.lastResponse = null;
    this.confirmResult = null;
    this.sessionLinkedInvoiceId = null;
    this.setPreviewInvoiceId(null);
    this.chatMessages = [];
    this.canContinueChat = true;
    this.pendingMultiOptionIds.clear();
    this.onboardingStep = 'idle';
    this.sessionMode = 'invoice';
    this.showReportSuggestions = false;
    this.pendingDraft = '';
    this.pendingImageFile = null;
    this.sessionDraftSnapshot = null;
    this.clearPersistedSession();
    this.cdr.markForCheck();
  }

  private invalidateLocalSessionKeepResult(): void {
    this.clearTimer();
    this.sessionId = null;
    this.activeSidebarSessionId = null;
    this.sessionDeadline = null;
    this.remainingLabel = '';
    this.lastResponse = null;
    this.sessionLinkedInvoiceId = null;
    this.canContinueChat = false;
  }

  private onSessionExpired(): void {
    this.toastr.warning(this.translate.instant('aiVoiceInvoice.sessionExpired'));
    this.canContinueChat = false;
    this.sessionDeadline = null;
    this.clearTimer();
    this.clearPersistedSession();
    this.cdr.markForCheck();
  }

  private startTimer(): void {
    this.clearTimer();
    this.timerHandle = setInterval(() => {
      if (!this.sessionDeadline) return;
      const left = this.sessionDeadline - Date.now();
      if (left <= 0) {
        this.clearTimer();
        this.onSessionExpired();
        return;
      }
      const m = Math.floor(left / 60000);
      const s = Math.floor((left % 60000) / 1000);
      this.remainingLabel = `${m}:${s.toString().padStart(2, '0')}`;
      this.cdr.markForCheck();
    }, 1000);
    const left0 = this.sessionDeadline! - Date.now();
    const m0 = Math.floor(left0 / 60000);
    const s0 = Math.floor((left0 % 60000) / 1000);
    this.remainingLabel = `${m0}:${s0.toString().padStart(2, '0')}`;
  }

  private clearTimer(): void {
    if (this.timerHandle) {
      clearInterval(this.timerHandle);
      this.timerHandle = null;
    }
  }

  /** Sync `invoice_id` from GET .../chat after a turn (no spinner). */
  private refreshSessionLinkedInvoice(): void {
    if (!this.sessionId) return;
    this.aiInvoiceVoice.getSessionChat(this.sessionId).subscribe({
      next: (r) => {
        const m = r.session as AiSessionChatMetaDto;
        const id = this.parseSessionInvoiceId(m.invoice_id);
        this.sessionLinkedInvoiceId = id;
        this.sessionDraftSnapshot = (m.draft_snapshot as Record<string, unknown>) ?? null;
        /* Always mirror the session's invoice_id — null clears the preview (invoice deleted). */
        this.setPreviewInvoiceId(id);
        this.cdr.markForCheck();
      },
      error: () => {},
    });
  }

  /* ================================================================
     Real-time invoice preview — GET /invoices-v2/:id/html?lang=...
     ================================================================ */

  /** Update the previewed invoice id; kick a fetch when id changes or when it is unchanged but the draft may have updated (same id). */
  private setPreviewInvoiceId(id: number | null): void {
    if (id != null && !Number.isFinite(Number(id))) id = null;
    if (id === this.previewInvoiceId) {
      /* Same invoice id — backend may have mutated lines/totals; always refetch HTML. */
      if (id != null) this.loadInvoicePreview(id);
      return;
    }
    this.previewInvoiceId = id;
    this.previewError = null;
    if (id == null) {
      this.previewSubscription?.unsubscribe();
      this.previewSubscription = undefined;
      this.previewSafeHtml = null;
      this.previewLoading = false;
      this.previewLastUpdatedAt = null;
      this.previewInvoiceMetaLoaded = false;
      this.previewInvoiceMetaAvailable = false;
      this.previewInvoiceConfirm = null;
      this.previewAllocationChecked = false;
      this.previewAllocationSaving = false;
      this.cdr.markForCheck();
      return;
    }
    this.loadInvoicePreview(id);
  }

  /** Re-fetch the current preview (manual refresh button). */
  refreshInvoicePreview(): void {
    if (this.previewInvoiceId != null) this.loadInvoicePreview(this.previewInvoiceId);
  }

  /* ================================================================
     Pre-session onboarding chat flow
     When user types before a session exists, we walk them through
     company → invoice type → document language as inline chat chips,
     then auto-start the session and dispatch their pending text.
     ================================================================ */

  /** Called from sendTextMessage / image upload when no session exists yet. */
  private kickOffOnboarding(draft: string): void {
    this.pendingDraft = draft;

    /* Single company — skip company step and start session immediately. */
    if (this.companies.length === 1) {
      this.selectedCompany = this.companies[0];
      this.onboardingStep = 'idle';
      this.isAiProcessing = true;
      this.startSessionThenSendPending();
      return;
    }

    this.onboardingStep = 'company';
    this.chatMessages.push({
      id: `onb-company-${Date.now()}`,
      role: 'system',
      turnId: 'onboarding',
      createdAt: Date.now(),
      text: this.translate.instant('aiVoiceInvoice.onboardingPickCompany'),
      setupStep: 'company',
    });
    this.scrollChatToBottom();
    this.cdr.markForCheck();
  }

  selectSetupCompany(c: Company): void {
    if (this.onboardingStep !== 'company') return;
    this.selectedCompany = c;
    const msg = this.chatMessages.find((m) => m.setupStep === 'company' && !m.setupCompleted);
    if (msg) msg.setupCompleted = true;
    this.chatMessages.push({
      id: `onb-company-reply-${Date.now()}`,
      role: 'merchant',
      turnId: 'onboarding-company',
      createdAt: Date.now(),
      text: c.label || c.name,
    });
    this.onboardingStep = 'idle';
    this.isAiProcessing = true;
    this.scrollChatToBottom();
    this.cdr.markForCheck();
    this.startSessionThenSendPending();
  }

  /** Start a session silently (no toastr) then send the user's original message. */
  private startSessionThenSendPending(): void {
    if (!this.selectedCompany?.value) {
      this.isAiProcessing = false;
      this.cdr.markForCheck();
      return;
    }
    const companyId = Number(this.selectedCompany.value);
    this.aiInvoiceVoice
      .startSession(this.buildStartSessionBody(companyId))
      .subscribe({
        next: (res) => {
          this.applyStartSessionResponse(res);
          this.confirmResult = null;
          this.lastResponse = null;
          this.sessionLinkedInvoiceId = null;
          this.sessionId = res.sessionId;
          this.activeSidebarSessionId = res.sessionId;
          this.canContinueChat = true;
          this.sessionDeadline = Date.now() + this.sessionTtlMs;
          this.persistSession(companyId);
          this.startTimer();
          this.refreshSessions();
          this.cdr.markForCheck();
          /* Pending image has priority over pending text. */
          if (this.pendingImageFile) {
            const file = this.pendingImageFile;
            this.pendingImageFile = null;
            const turnId = this.newChatTurnId();
            this.isUploadingImage = true;
            this.dispatchImageToApi(file, turnId);
          } else {
            const draft = this.pendingDraft.trim();
            this.pendingDraft = '';
            if (draft) {
              /* Dispatch the pending text; merchant bubble was already added at onboarding start. */
              this.dispatchTextToApi(draft);
            } else {
              /* Proactive onboarding — session ready, show greeting so user knows to start typing. */
              this.isAiProcessing = false;
              this.appendSessionReadyGreeting();
            }
          }
        },
        error: (err: HttpErrorResponse) => {
          this.isAiProcessing = false;
          this.toastr.error(this.httpErrorMessage(err, 'aiVoiceInvoice.startSessionError'));
          this.cdr.markForCheck();
        },
      });
  }

  /** Append a local AI greeting bubble after proactive onboarding completes. */
  private appendSessionReadyGreeting(): void {
    const key =
      this.sessionMode === 'report'
        ? 'aiVoiceInvoice.sessionReadyGreetingReport'
        : 'aiVoiceInvoice.sessionReadyGreeting';
    this.chatMessages.push({
      id: `greeting-${Date.now()}`,
      role: 'system',
      turnId: this.newChatTurnId(),
      createdAt: Date.now(),
      text: this.translate.instant(key),
    });
    if (this.sessionMode === 'report') {
      this.showReportSuggestions = true;
    }
    this.scrollChatToBottom();
    this.cdr.markForCheck();
  }

  trackByMessageId(_index: number, m: VoiceChatMessage): string {
    return m.id;
  }

  reportExportHasData(m: VoiceChatMessage): boolean {
    const rd = m.voiceResponse?.report_data;
    return !!rd?.columns?.length && !!rd.rows?.length;
  }

  downloadReportExcel(m: VoiceChatMessage): void {
    const rd = m.voiceResponse?.report_data;
    if (!rd?.columns?.length || !rd.rows?.length) {
      this.toastr.warning(this.translate.instant('aiVoiceInvoice.reportExportNoData'));
      return;
    }
    const ws = this.buildReportExcelWorksheet(rd);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, this.reportExcelSheetName());
    const name = this.reportExportFileBase();
    XLSX.writeFile(wb, `${name}.xlsx`);
    this.toastr.success(this.translate.instant('aiVoiceInvoice.reportExportExcelOk'));
  }

  /** Richer Excel: merged header, column widths, auto-filter, freeze panes, numeric cells. */
  private buildReportExcelWorksheet(rd: ReportDataDto): XLSX.WorkSheet {
    const cols = rd.columns;
    const colCount = cols.length;
    const locale =
      this.curentLang === 'ar' ? 'ar' : this.curentLang === 'he' ? 'he-IL' : 'en-GB';
    const metaLines = [
      this.translate.instant('aiVoiceInvoice.reportExportFooter'),
      this.translate.instant('aiVoiceInvoice.reportExcelRowCountLine', {
        count: rd.row_count ?? rd.rows.length,
      }),
      this.translate.instant('aiVoiceInvoice.reportExcelGeneratedAt', {
        when: new Date().toLocaleString(locale),
      }),
    ];
    const metaCell = metaLines.join('\n');

    const headerRow = cols.map((c) => c);
    const dataRows = rd.rows.map((r) => cols.map((c) => this.coerceReportCell(r[c])));
    const pad = (n: number) => Array.from({ length: Math.max(0, n) }, () => '');
    const aoa: (string | number)[][] = [[metaCell, ...pad(colCount - 1)], headerRow, ...dataRows];

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const lastDataRow = 1 + rd.rows.length;

    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } }];
    ws['!cols'] = this.reportExcelColumnWidths(rd);
    ws['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: 1, c: 0 },
        e: { r: lastDataRow, c: colCount - 1 },
      }),
    };
    ws['!views'] = [
      {
        ySplit: 2,
        xSplit: 0,
        topLeftCell: XLSX.utils.encode_cell({ r: 2, c: 0 }),
        activePane: 'bottomLeft',
        state: 'frozen',
      },
    ];

    return ws;
  }

  private coerceReportCell(value: unknown): string | number {
    if (value == null || value === '') return '';
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const s = String(value).trim();
    if (s === '') return '';
    const arabicComma = /\u066C/g;
    const arabicDecimal = /\u066B/g;
    const normalized = s
      .replace(arabicComma, ',')
      .replace(arabicDecimal, '.')
      .replace(/,/g, '')
      .replace(/\s/g, '');
    if (/^-?\d+(\.\d+)?$/.test(normalized)) {
      const n = Number(normalized);
      if (!Number.isNaN(n)) return n;
    }
    return s;
  }

  private reportExcelColumnWidths(rd: ReportDataDto): XLSX.ColInfo[] {
    return rd.columns.map((key) => {
      let maxLen = String(key).length;
      for (const row of rd.rows) {
        maxLen = Math.max(maxLen, String(row[key] ?? '').length);
      }
      return { wch: Math.min(52, Math.max(12, maxLen + 2)) };
    });
  }

  private reportExcelSheetName(): string {
    const raw = this.translate.instant('aiVoiceInvoice.reportExcelSheetName');
    const cleaned = String(raw || 'Report').replace(/[:\\/?*[\]]/g, '-').trim();
    return cleaned.slice(0, 31) || 'Report';
  }

  private reportExportFileBase(): string {
    const sid = (this.sessionId || 'export').replace(/[^\w-]+/g, '').slice(0, 12);
    return `fawtara-report-${sid}-${Date.now()}`;
  }

  /**
   * Send text directly to the API without adding a merchant bubble
   * (used when the bubble was already pre-emptively appended, e.g. during onboarding).
   */
  private dispatchTextToApi(text: string): void {
    if (!this.sessionId || !text) {
      this.isAiProcessing = false;
      this.cdr.markForCheck();
      return;
    }
    this.aiInvoiceVoice.postText(this.sessionId, { text }).subscribe({
      next: (res) => {
        this.isAiProcessing = false;
        this.lastResponse = res;
        /* omitMerchantBubble — we already have it from before onboarding. */
        this.appendVoiceTurnFromResponse(res, { omitMerchantBubble: true });
        this.afterVoiceStatus(res.status);
        this.refreshSessions();
        this.refreshSessionLinkedInvoice();
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.isAiProcessing = false;
        if (err.status === 404) {
          this.toastr.error(this.translate.instant('aiVoiceInvoice.sessionNotFound'));
          this.endSession();
        } else {
          this.toastr.error(this.httpErrorMessage(err, 'aiVoiceInvoice.textError'));
        }
        this.cdr.markForCheck();
      },
    });
  }

  private get previewLang(): string {
    return (this.documentLanguage || this.curentLang || 'ar').toLowerCase();
  }

  private loadInvoicePreview(id: number): void {
    this.previewLoading = true;
    this.previewError = null;
    this.previewInvoiceMetaLoaded = false;
    this.previewInvoiceMetaAvailable = false;
    this.previewSubscription?.unsubscribe();
    this.previewSubscription = forkJoin({
      html: this.invoiceV2.getInvoiceHtml(id, this.previewLang),
      meta: this.invoiceV2.getInvoiceById(id).pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({ html, meta }) => {
        const enriched = this.preparePreviewDocument(html);
        this.previewSafeHtml = this.sanitizer.bypassSecurityTrustHtml(enriched);
        this.previewLoading = false;
        this.previewLastUpdatedAt = Date.now();
        this.applyPreviewInvoiceMeta(meta);
        this.previewInvoiceMetaLoaded = true;
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.previewLoading = false;
        this.previewError = this.httpErrorMessage(err, 'aiVoiceInvoice.previewError');
        this.previewSafeHtml = null;
        this.previewInvoiceMetaLoaded = true;
        this.previewInvoiceMetaAvailable = false;
        this.previewInvoiceConfirm = null;
        this.previewAllocationChecked = false;
        this.cdr.markForCheck();
      },
    });
  }

  /** Parse GET /invoices-v2/:id for confirm + allocation_number (preview toolbar). */
  private applyPreviewInvoiceMeta(response: unknown): void {
    if (!response) {
      this.previewInvoiceMetaAvailable = false;
      this.previewInvoiceConfirm = null;
      this.previewAllocationChecked = false;
      return;
    }
    const inv =
      response &&
      typeof response === 'object' &&
      'data' in (response as Record<string, unknown>) &&
      (response as { data?: unknown }).data != null
        ? (response as { data: Record<string, unknown> }).data
        : (response as Record<string, unknown>);
    if (!inv || typeof inv !== 'object') {
      this.previewInvoiceMetaAvailable = false;
      this.previewInvoiceConfirm = null;
      this.previewAllocationChecked = false;
      return;
    }
    this.previewInvoiceMetaAvailable = true;
    const confirmRaw = inv['confirm'];
    const confirmValue =
      confirmRaw === 1 ||
      confirmRaw === true ||
      Number(confirmRaw) === 1;
    this.previewInvoiceConfirm = confirmValue ? 1 : 0;
    const alloc = inv['allocation_number'];
    this.previewAllocationChecked = alloc === 1 || alloc === true || Number(alloc) === 1;
  }

  /**
   * Checkbox: send to tax authority on confirmation — PUT …/allocation-number (draft invoices only).
   */
  onPreviewAllocationToggle(ev: Event): void {
    const id = this.previewInvoiceId;
    const input = ev.target as HTMLInputElement | null;
    if (id == null || input == null || !this.previewInvoiceMetaAvailable) {
      return;
    }
    if (this.previewInvoiceConfirm !== 0) {
      input.checked = this.previewAllocationChecked;
      return;
    }
    const previousChecked = this.previewAllocationChecked;
    const checked = input.checked;
    this.previewAllocationSaving = true;
    const nextAlloc: 0 | 1 = checked ? 1 : 0;
    this.invoiceV2.updateAllocationNumber(id, nextAlloc).subscribe({
      next: () => {
        this.previewAllocationSaving = false;
        this.previewAllocationChecked = checked;
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.previewAllocationSaving = false;
        input.checked = previousChecked;
        this.toastr.error(
          this.httpErrorMessage(err, 'aiVoiceInvoice.previewAllocationUpdateError'),
        );
        this.cdr.markForCheck();
      },
    });
  }

  /** Show allocation row once invoice HTML + meta finished loading successfully enough to read flags. */
  get showPreviewAllocationBar(): boolean {
    return (
      this.previewInvoiceId != null &&
      this.previewInvoiceMetaLoaded &&
      this.previewInvoiceMetaAvailable &&
      !this.previewError
    );
  }

  /** Draft invoice — user may toggle allocation (matches invoices-v2-form). */
  get previewAllocationEditable(): boolean {
    return this.previewInvoiceConfirm === 0 && !this.previewAllocationSaving;
  }

  /**
   * Invoice HTML often clips itself (fixed height + overflow:hidden), so the
   * iframe never becomes scrollable. Unlock those boxes once the frame loads.
   */
  onPreviewFrameLoad(ev: Event): void {
    const iframe = ev.target as HTMLIFrameElement | null;
    const doc = iframe?.contentDocument;
    const win = iframe?.contentWindow;
    if (!iframe || !doc?.body || !win || !doc.documentElement) return;

    const unlock = doc.getElementById('ai-preview-scroll-unlock') ?? doc.createElement('style');
    unlock.id = 'ai-preview-scroll-unlock';
    unlock.textContent = [
      'html,body{height:auto!important;max-height:none!important;overflow-x:auto!important;overflow-y:auto!important;overscroll-behavior:contain;touch-action:none;}',
      'body{position:relative!important;}',
      'html,body,body *{cursor:grab!important;background-attachment:scroll!important;}',
      'a,button,input,textarea,select,label,a *,button *{cursor:pointer!important;}',
    ].join('');
    doc.head?.appendChild(unlock);

    const rtl =
      win.getComputedStyle(doc.body).direction === 'rtl' ||
      doc.documentElement.getAttribute('dir') === 'rtl' ||
      doc.body.getAttribute('dir') === 'rtl';
    /* The scrollport must be left-to-right. In an RTL document Chrome keeps
       scrollLeft at 0 for both drag directions, so the hand cannot pan sideways.
       The invoice body stays RTL so the text layout does not flip. */
    if (rtl) {
      doc.documentElement.style.setProperty('direction', 'ltr', 'important');
      doc.body.style.setProperty('direction', 'rtl', 'important');
    }

    const nodes: HTMLElement[] = [doc.documentElement, doc.body];
    doc.body.querySelectorAll<HTMLElement>('*').forEach((el) => nodes.push(el));
    let contentWidth = doc.documentElement.clientWidth;
    nodes.forEach((el) => {
      const cs = win.getComputedStyle(el);
      /* Fixed/sticky stamps stay glued to the preview window and slide over the invoice. */
      if (cs.position === 'fixed' || cs.position === 'sticky') {
        el.style.setProperty('position', 'absolute', 'important');
      }
      const clipsY = cs.overflowY === 'hidden' || cs.overflowY === 'clip';
      if (clipsY && el.scrollHeight > el.clientHeight + 8) {
        el.style.setProperty('overflow-y', 'auto', 'important');
        el.style.setProperty('height', 'auto', 'important');
        el.style.setProperty('max-height', 'none', 'important');
      }
      /* Keep sideways overflow scrollable. A shorthand overflow:visible was wiping it. */
      if (el.scrollWidth > el.clientWidth + 2) {
        el.style.setProperty('overflow-x', 'auto', 'important');
        el.style.setProperty('max-width', 'none', 'important');
      }
      contentWidth = Math.max(contentWidth, el.scrollWidth, el.getBoundingClientRect().width);
    });
    if (contentWidth > doc.documentElement.clientWidth + 2) {
      doc.body.style.setProperty('min-width', `${Math.ceil(contentWidth)}px`, 'important');
    }

    this.bindPreviewDragScroll(doc);
  }

  /** Drag the invoice with the hand cursor, up/down and left/right. Wheel scrolling stays available. */
  private bindPreviewDragScroll(doc: Document): void {
    const root = doc.documentElement;
    if (root.dataset['aiPreviewDrag'] === '1') return;
    root.dataset['aiPreviewDrag'] = '1';

    let dragging = false;
    let startX = 0;
    let startY = 0;
    let scrollers: { el: HTMLElement; left: number; top: number }[] = [];

    const setGrabbing = (on: boolean) => {
      root.style.cursor = on ? 'grabbing' : '';
      doc.body.style.cursor = on ? 'grabbing' : '';
    };

    const captureScrollers = (from: EventTarget | null): void => {
      const seen = new Set<HTMLElement>();
      scrollers = [];
      const add = (el: HTMLElement | null) => {
        if (!el || seen.has(el)) return;
        seen.add(el);
        scrollers.push({ el, left: el.scrollLeft, top: el.scrollTop });
      };
      let node = from instanceof HTMLElement ? from : doc.body;
      while (node) {
        add(node);
        node = node.parentElement;
      }
      add(doc.scrollingElement instanceof HTMLElement ? doc.scrollingElement : null);
      add(doc.documentElement);
      add(doc.body);
    };

    doc.addEventListener('pointerdown', (ev: PointerEvent) => {
      if (ev.button !== 0) return;
      const el = ev.target as HTMLElement | null;
      if (el?.closest('a, button, input, textarea, select, label')) return;
      dragging = true;
      startX = ev.clientX;
      startY = ev.clientY;
      captureScrollers(ev.target);
      setGrabbing(true);
      try {
        el?.setPointerCapture?.(ev.pointerId);
      } catch {
        /* iframe documents can reject capture; pointermove on the document still tracks */
      }
      ev.preventDefault();
    });

    doc.addEventListener('pointermove', (ev: PointerEvent) => {
      if (!dragging) return;
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      scrollers.forEach(({ el, left, top }) => {
        el.scrollLeft = left - dx;
        el.scrollTop = top - dy;
      });
      ev.preventDefault();
    });

    const end = () => {
      if (!dragging) return;
      dragging = false;
      setGrabbing(false);
    };
    doc.addEventListener('pointerup', end);
    doc.addEventListener('pointercancel', end);
  }

  /** Inject `<base>` so relative URLs (logos, CSS) resolve against the API host. */
  private preparePreviewDocument(html: string): string {
    if (!html) return '';
    const baseTag = `<base href="${baseUrl}">`;
    /* Invoice HTML often locks the page at 100% height with overflow hidden.
       Force the document to grow and scroll inside the preview iframe. */
    const scrollStyle =
      '<style id="ai-preview-scroll">html,body{height:auto!important;min-height:100%!important;max-height:none!important;overflow-x:auto!important;overflow-y:auto!important;overscroll-behavior:contain;touch-action:none;cursor:grab;}html{scrollbar-width:thin;}body{position:relative!important;}</style>';
    let doc = html;
    if (!/<base\s/i.test(doc)) {
      if (/<head[^>]*>/i.test(doc)) {
        doc = doc.replace(/<head[^>]*>/i, (m) => `${m}${baseTag}`);
      } else if (/<html[^>]*>/i.test(doc)) {
        doc = doc.replace(/<html[^>]*>/i, (m) => `${m}<head>${baseTag}</head>`);
      } else {
        doc = `<!doctype html><html><head>${baseTag}</head><body>${doc}</body></html>`;
      }
    }
    if (/<head[^>]*>/i.test(doc)) {
      return doc.replace(/<head[^>]*>/i, (m) => `${m}${scrollStyle}`);
    }
    return `${scrollStyle}${doc}`;
  }

  /** Find the most recent `invoice_result.invoice_id` in chat history rows. */
  private findLatestInvoiceIdInRows(rows: AiServerChatMessageDto[]): number | null {
    for (let i = rows.length - 1; i >= 0; i--) {
      const m = rows[i];
      if (m.role !== 'system') continue;
      const meta = m.metadata as Partial<AiVoiceResponseDto> | undefined;
      const id = meta?.invoice_result?.invoice_id;
      if (id != null && Number.isFinite(Number(id))) return Number(id);
    }
    return null;
  }

  /** True while the iframe should display its loading veil. */
  get isPreviewBusy(): boolean {
    return this.previewLoading || (this.isAiProcessing && this.previewInvoiceId != null);
  }

  /** Index of the latest system bubble that still needs a list choice (single or multi). */
  private lastNeedSelectionMessageIndex(): number {
    for (let j = this.chatMessages.length - 1; j >= 0; j--) {
      const row = this.chatMessages[j];
      if (
        row.role === 'system' &&
        row.voiceResponse?.status === 'need_selection' &&
        row.voiceResponse.options?.length
      ) {
        return j;
      }
    }
    return -1;
  }

  /** Only the current `need_selection` row accepts taps — older suggestion rows stay disabled. */
  canInteractWithOptions(m: VoiceChatMessage): boolean {
    if (!this.sessionId || !this.canSendMessages || this.isAiProcessing) return false;
    const i = this.lastNeedSelectionMessageIndex();
    if (i < 0) return false;
    return this.chatMessages[i] === m;
  }

  /** Index of the latest assistant bubble that still expects a quick-reply tap. */
  private lastQuickReplyMessageIndex(): number {
    for (let j = this.chatMessages.length - 1; j >= 0; j--) {
      const row = this.chatMessages[j];
      if (row.role === 'system' && this.computeQuickReplyButtons(row).length > 0) {
        return j;
      }
    }
    return -1;
  }

  /** Only the current quick-reply row accepts taps — older prompts stay disabled. */
  canInteractWithQuickReplies(m: VoiceChatMessage): boolean {
    if (!this.sessionId || !this.canSendMessages || this.isAiProcessing) return false;
    if (m.role !== 'system' || m.isConfirmSuccess || m.setupStep || m.reportSafeHtml) return false;
    const i = this.lastQuickReplyMessageIndex();
    return i >= 0 && this.chatMessages[i] === m;
  }

  getQuickReplyButtons(m: VoiceChatMessage): VoiceQuickReplyButton[] {
    return this.computeQuickReplyButtons(m);
  }

  private computeQuickReplyButtons(m: VoiceChatMessage): VoiceQuickReplyButton[] {
    if (m.role !== 'system' || m.isConfirmSuccess || m.setupStep || m.reportSafeHtml) return [];

    const yesText = this.yesReplyText();
    const noText = this.noReplyText();
    const vr = m.voiceResponse;

    if (vr?.open_modal_create_customer && !vr.options?.length) {
      return [
        {
          id: 'add-customer',
          labelKey: 'aiVoiceInvoice.replyAddCustomer',
          variant: 'primary',
          icon: 'fa-user-plus',
          action: 'open_customer_modal',
        },
        {
          id: 'no',
          labelKey: 'aiVoiceInvoice.replyNo',
          sendText: noText,
          variant: 'secondary',
          icon: 'fa-times',
        },
      ];
    }

    /* Options present (incl. local AIBOT-3 matches) — still offer create when flagged. */
    if (vr?.open_modal_create_customer && vr.options?.length) {
      return [
        {
          id: 'add-customer',
          labelKey: 'aiVoiceInvoice.replyAddCustomer',
          variant: 'secondary',
          icon: 'fa-user-plus',
          action: 'open_customer_modal',
        },
      ];
    }

    if (vr?.open_modal_create_product && !vr.options?.length) {
      return [
        {
          id: 'add-product',
          labelKey: 'aiVoiceInvoice.replyAddProduct',
          variant: 'primary',
          icon: 'fa-cube',
          action: 'open_product_modal',
        },
        {
          id: 'no',
          labelKey: 'aiVoiceInvoice.replyNo',
          sendText: noText,
          variant: 'secondary',
          icon: 'fa-times',
        },
      ];
    }

    if (this.isPendingCreateConfirm(m) || this.isDeleteDraftConfirm(m)) {
      return [
        {
          id: 'yes',
          labelKey: 'aiVoiceInvoice.replyYes',
          sendText: yesText,
          variant: 'primary',
          icon: 'fa-check',
        },
        {
          id: 'no',
          labelKey: 'aiVoiceInvoice.replyNo',
          sendText: noText,
          variant: 'secondary',
          icon: 'fa-times',
        },
      ];
    }

    if (vr?.status === 'missing_data' && vr.missing?.includes('customer_vat')) {
      const msg = vr.message || m.text || '';
      if (/تخطي|skip|דלג/i.test(msg)) {
        return [
          {
            id: 'skip-vat',
            labelKey: 'aiVoiceInvoice.replySkipVat',
            sendText: this.skipVatReplyText(),
            variant: 'secondary',
            icon: 'fa-forward',
          },
        ];
      }
    }

    return [];
  }

  private messageAsksYesNo(text: string): boolean {
    const t = (text ?? '').trim();
    if (!t) return false;
    return (
      /\(\s*(?:نعم|yes|כן)\s*\/\s*(?:لا|no|לא)\s*\)/i.test(t) ||
      /(?:«(?:yes|no)»|«(?:نعم|لا)»|«(?:כן|לא)»)/i.test(t) ||
      /(?:اكتب|Reply|כתוב).*(?:نعم|yes|כן).*(?:لا|no|לא)/i.test(t)
    );
  }

  private isPendingCreateConfirm(m: VoiceChatMessage): boolean {
    const vr = m.voiceResponse;
    if (!vr || vr.options?.length) return false;
    const msg = vr.message || m.text || '';
    if (!this.messageAsksYesNo(msg)) return false;

    const missing = vr.missing ?? [];
    if (
      missing.includes('customer') &&
      /لم نجد عميل|No customer named|לא נמצא לקוח/i.test(msg)
    ) {
      return true;
    }
    return missing.some((x) => x.startsWith('product_not_found:'));
  }

  private isDeleteDraftConfirm(m: VoiceChatMessage): boolean {
    const vr = m.voiceResponse;
    if (!vr || vr.options?.length) return false;
    const msg = vr.message || m.text || '';
    if (!this.messageAsksYesNo(msg)) return false;
    return /حذف.*(?:نهائي|الفاتورة)|permanently delete|למחוק לצמיתות/i.test(msg);
  }

  private yesReplyText(): string {
    const lang = (this.translate.currentLang || 'ar').toLowerCase();
    if (lang.startsWith('he')) return 'כן';
    if (lang.startsWith('en')) return 'yes';
    return 'نعم';
  }

  private noReplyText(): string {
    const lang = (this.translate.currentLang || 'ar').toLowerCase();
    if (lang.startsWith('he')) return 'לא';
    if (lang.startsWith('en')) return 'no';
    return 'لا';
  }

  private skipVatReplyText(): string {
    const lang = (this.translate.currentLang || 'ar').toLowerCase();
    if (lang.startsWith('he')) return 'דלג';
    if (lang.startsWith('en')) return 'skip';
    return 'تخطي';
  }

  sendQuickReply(m: VoiceChatMessage, btn: VoiceQuickReplyButton): void {
    if (!this.canInteractWithQuickReplies(m)) return;

    if (btn.action === 'open_customer_modal') {
      const vr = m.voiceResponse;
      this.pendingSuggestedCustomerName = vr?.suggested_customer_name?.trim() ?? null;
      this.pendingSuggestedCustomerDetails = vr?.suggested_customer_details ?? null;
      this.showAddCustomerModal = true;
      this.showAddProductModal = false;
      this.cdr.markForCheck();
      return;
    }

    if (btn.action === 'open_product_modal') {
      const vr = m.voiceResponse;
      this.pendingSuggestedProductName = vr?.suggested_product_name?.trim() ?? null;
      this.pendingSuggestedProductDetails = vr?.suggested_product_details ?? null;
      this.showAddProductModal = true;
      this.showAddCustomerModal = false;
      this.cdr.markForCheck();
      return;
    }

    const text = btn.sendText?.trim();
    if (!text || !this.sessionId) return;
    if (!this.canSendMessages) {
      this.toastr.warning(this.translate.instant('aiVoiceInvoice.readOnlySession'));
      return;
    }

    const turnId = this.newChatTurnId();
    const ts = Date.now();
    const displayText = this.translate.instant(btn.labelKey);
    this.chatMessages.push({
      id: `merchant-${turnId}`,
      role: 'merchant',
      turnId,
      createdAt: ts,
      text: displayText,
    });
    this.pendingMultiOptionIds.clear();
    this.isAiProcessing = true;
    this.lastResponse = null;
    this.cdr.markForCheck();
    this.scrollChatToBottom();

    this.aiInvoiceVoice.postText(this.sessionId, { text }).subscribe({
      next: (res) => {
        this.isAiProcessing = false;
        this.lastResponse = res;
        this.appendVoiceTurnFromResponse(res, { linkTurnId: turnId, omitMerchantBubble: true });
        this.afterVoiceStatus(res.status);
        this.refreshSessions();
        this.refreshSessionLinkedInvoice();
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.isAiProcessing = false;
        if (err.status === 404) {
          this.toastr.error(this.translate.instant('aiVoiceInvoice.sessionNotFound'));
          this.endSession();
        } else {
          this.toastr.error(this.httpErrorMessage(err, 'aiVoiceInvoice.textError'));
        }
        this.cdr.markForCheck();
      },
    });
  }

  isMultiOptionSelected(id: number): boolean {
    return this.pendingMultiOptionIds.has(id);
  }

  pendingMultiSelectEmpty(): boolean {
    return this.pendingMultiOptionIds.size === 0;
  }

  toggleMultiOption(row: VoiceChatMessage, optionId: number, checked: boolean): void {
    if (!this.canInteractWithOptions(row)) return;
    if (checked) this.pendingMultiOptionIds.add(optionId);
    else this.pendingMultiOptionIds.delete(optionId);
    this.cdr.markForCheck();
  }

  /** Submit several ids when `multi_select` is true (`selected_option_ids` on POST .../text). */
  submitMultiSelection(): void {
    const idx = this.lastNeedSelectionMessageIndex();
    const row = idx >= 0 ? this.chatMessages[idx] : null;
    if (!row?.voiceResponse?.multi_select || !this.canInteractWithOptions(row)) return;
    if (!this.sessionId) {
      this.toastr.warning(this.translate.instant('aiVoiceInvoice.needSession'));
      return;
    }
    if (!this.canSendMessages) {
      this.toastr.warning(this.translate.instant('aiVoiceInvoice.readOnlySession'));
      return;
    }
    const ids = Array.from(this.pendingMultiOptionIds).sort((a, b) => a - b);
    if (!ids.length) {
      this.toastr.warning(this.translate.instant('aiVoiceInvoice.multiSelectPickAtLeastOne'));
      return;
    }
    const turnId = this.newChatTurnId();
    const ts = Date.now();
    this.chatMessages.push({
      id: `merchant-${turnId}`,
      role: 'merchant',
      turnId,
      createdAt: ts,
      text: this.multiSelectionMerchantSummary(ids),
    });
    this.isAiProcessing = true;
    this.lastResponse = null;
    this.cdr.markForCheck();
    this.scrollChatToBottom();
    this.aiInvoiceVoice.postText(this.sessionId, { selected_option_ids: ids }).subscribe({
      next: (res) => {
        this.isAiProcessing = false;
        this.lastResponse = res;
        this.appendVoiceTurnFromResponse(res, { linkTurnId: turnId, omitMerchantBubble: true });
        this.afterVoiceStatus(res.status);
        this.refreshSessions();
        this.refreshSessionLinkedInvoice();
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.isAiProcessing = false;
        if (err.status === 404) {
          this.toastr.error(this.translate.instant('aiVoiceInvoice.sessionNotFound'));
          this.endSession();
        } else {
          this.toastr.error(this.httpErrorMessage(err, 'aiVoiceInvoice.textError'));
        }
        this.cdr.markForCheck();
      },
    });
  }

  private multiSelectionMerchantSummary(ids: number[]): string {
    const i = this.lastNeedSelectionMessageIndex();
    const opts = i >= 0 ? this.chatMessages[i].voiceResponse?.options ?? [] : [];
    const labels = ids
      .map((id) => opts.find((o) => o.id === id)?.label?.trim())
      .filter((x): x is string => !!x);
    const joined = labels.slice(0, 8).join(', ') + (labels.length > 8 ? '…' : '');
    return this.translate.instant('aiVoiceInvoice.selectionMultipleSummary', {
      count: ids.length,
      labels: joined || '—',
    });
  }

  /** Submit a list option from `need_selection` via `selected_option_id` (with or without typing). */
  selectOption(row: VoiceChatMessage, option: AiVoiceOptionDto): void {
    if (row.voiceResponse?.multi_select) return;
    if (!this.canInteractWithOptions(row)) return;
    if (!this.sessionId) {
      this.toastr.warning(this.translate.instant('aiVoiceInvoice.needSession'));
      return;
    }
    if (!this.canSendMessages) {
      this.toastr.warning(this.translate.instant('aiVoiceInvoice.readOnlySession'));
      return;
    }
    this.pendingMultiOptionIds.clear();
    const turnId = this.newChatTurnId();
    const ts = Date.now();
    this.chatMessages.push({
      id: `merchant-${turnId}`,
      role: 'merchant',
      turnId,
      createdAt: ts,
      text: this.optionSelectionLabel(option),
    });
    this.isAiProcessing = true;
    this.lastResponse = null;
    this.cdr.markForCheck();
    this.scrollChatToBottom();
    this.aiInvoiceVoice.postText(this.sessionId, { selected_option_id: option.id }).subscribe({
      next: (res) => {
        this.isAiProcessing = false;
        this.lastResponse = res;
        this.appendVoiceTurnFromResponse(res, { linkTurnId: turnId, omitMerchantBubble: true });
        this.afterVoiceStatus(res.status);
        this.refreshSessions();
        this.refreshSessionLinkedInvoice();
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.isAiProcessing = false;
        if (err.status === 404) {
          this.toastr.error(this.translate.instant('aiVoiceInvoice.sessionNotFound'));
          this.endSession();
        } else {
          this.toastr.error(this.httpErrorMessage(err, 'aiVoiceInvoice.textError'));
        }
        this.cdr.markForCheck();
      },
    });
  }

  private newChatTurnId(): string {
    return typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `turn-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }

  /** Merchant bubble text when picking a suggested customer/product (shown before the API reply). */
  private optionSelectionLabel(option: AiVoiceOptionDto): string {
    const name = (option.label || '').trim() || '—';
    if (option.kind === 'product') {
      return this.translate.instant('aiVoiceInvoice.selectionChoseProduct', { name });
    }
    return this.translate.instant('aiVoiceInvoice.selectionChoseCustomer', { name });
  }

  /** Enter sends; Shift+Enter newline; ArrowUp recalls last user message when draft empty. */
  onComposerKeydown(ev: KeyboardEvent): void {
    if (ev.key === 'ArrowUp') {
      const draftEmpty = !(this.draftText || '').trim();
      if (
        draftEmpty &&
        !this.isAiProcessing &&
        !this.sessionIsReadOnly
      ) {
        const recalled = this.getLastMerchantMessageForRecall();
        if (recalled != null) {
          ev.preventDefault();
          this.draftText = recalled;
          this.cdr.markForCheck();
          setTimeout(() => {
            const el = this.composerTextarea?.nativeElement;
            if (el) {
              el.focus();
              const len = el.value.length;
              el.setSelectionRange(len, len);
            }
          }, 0);
        }
      }
      return;
    }
    if (ev.key !== 'Enter' || ev.shiftKey) return;
    ev.preventDefault();
    this.sendTextMessage();
  }

  /** Last merchant bubble text (user messages in this AI chat). */
  private getLastMerchantMessageForRecall(): string | null {
    for (let i = this.chatMessages.length - 1; i >= 0; i--) {
      const m = this.chatMessages[i];
      if (m.role !== 'merchant') continue;
      const t = (m.text ?? '').trim();
      if (!t) continue;
      return m.text ?? '';
    }
    return null;
  }

  /** Prefill composer from a report suggestion (do not auto-send — chat stays primary). */
  sendReportSuggestion(prompt: string): void {
    this.showReportSuggestions = false;
    this.clickExample(prompt);
  }

  sendTextMessage(): void {
    const t = this.draftText?.trim();
    if (!t) return;
    this.showReportSuggestions = false;

    /* ---- Pre-session: intercept and start onboarding flow ---- */
    if (!this.sessionId) {
      if (!this.bootstrapLoaded) {
        this.toastr.info(this.translate.instant('aiVoiceInvoice.loading'));
        return;
      }
      if (this.isAiProcessing) return;
      /* Waiting for company pick — append message and update pending draft. */
      if (this.onboardingStep === 'company') {
        this.pendingDraft = t;
        const turnId = this.newChatTurnId();
        this.chatMessages.push({
          id: `merchant-${turnId}`,
          role: 'merchant',
          turnId,
          createdAt: Date.now(),
          text: t,
        });
        this.draftText = '';
        this.scrollChatToBottom();
        this.cdr.markForCheck();
        return;
      }
      /* Add merchant bubble immediately so the user sees their message. */
      const turnId = this.newChatTurnId();
      this.chatMessages.push({
        id: `merchant-${turnId}`,
        role: 'merchant',
        turnId,
        createdAt: Date.now(),
        text: t,
      });
      this.draftText = '';
      this.kickOffOnboarding(t);
      return;
    }

    if (!this.canSendMessages) {
      this.toastr.warning(this.translate.instant('aiVoiceInvoice.readOnlySession'));
      return;
    }
    this.pendingMultiOptionIds.clear();
    this.isAiProcessing = true;
    this.lastResponse = null;
    this.cdr.markForCheck();
    this.scrollChatToBottom();
    this.aiInvoiceVoice.postText(this.sessionId, { text: t }).subscribe({
      next: (res) => {
        this.isAiProcessing = false;
        this.draftText = '';
        this.lastResponse = res;
        this.appendVoiceTurnFromResponse(res);
        this.afterVoiceStatus(res.status);
        this.refreshSessions();
        this.refreshSessionLinkedInvoice();
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.isAiProcessing = false;
        if (err.status === 404) {
          this.toastr.error(this.translate.instant('aiVoiceInvoice.sessionNotFound'));
          this.endSession();
        } else {
          this.toastr.error(this.httpErrorMessage(err, 'aiVoiceInvoice.textError'));
        }
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Dictation via Web Speech API — ends up filling `draftText` which is sent through the
   * backend-supported `/text` endpoint. No audio is uploaded: the backend contract is text-only.
   */
  toggleRecording(): void {
    if (this.sessionId && !this.canSendMessages) {
      this.toastr.warning(this.translate.instant('aiVoiceInvoice.readOnlySession'));
      return;
    }
    if (!this.supportsBrowserStt) {
      this.micError = this.translate.instant('aiVoiceInvoice.sttUnsupported');
      this.toastr.warning(this.micError || '');
      return;
    }
    if (this.isRecording) {
      this.stopRecording();
      return;
    }
    this.micError = null;
    this.startBrowserSpeechToText();
  }

  private getSpeechRecognitionCtor(): (new () => WebSpeechRecognition) | null {
    if (typeof window === 'undefined') return null;
    const w = window as Window & {
      SpeechRecognition?: new () => WebSpeechRecognition;
      webkitSpeechRecognition?: new () => WebSpeechRecognition;
    };
    return w.SpeechRecognition || w.webkitSpeechRecognition || null;
  }

  /** BCP-47 tag for Web Speech API — follows document language when possible. */
  private recognitionLang(): string {
    const d = (this.documentLanguage || 'ar').toLowerCase().slice(0, 2);
    if (d === 'he') return 'he-IL';
    if (d === 'en') return 'en-US';
    return 'ar-SA';
  }

  private startBrowserSpeechToText(): void {
    const Ctor = this.getSpeechRecognitionCtor();
    if (!Ctor) return;
    this.sttErrored = false;
    this.sttFinalAccum = '';
    this.draftText = '';
    const rec = new Ctor();
    this.speechRecognition = rec;
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = this.recognitionLang();

    rec.onresult = (event: Event) => {
      const ev = event as unknown as {
        resultIndex: number;
        results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }>;
      };
      let interim = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const r = ev.results[i];
        const piece = r[0]?.transcript ?? '';
        if (r.isFinal) {
          this.sttFinalAccum += piece;
        } else {
          interim += piece;
        }
      }
      this.draftText = (this.sttFinalAccum + interim).trim();
      this.cdr.markForCheck();
    };

    rec.onerror = (event: Event) => {
      const err = (event as { error?: string }).error;
      if (err === 'aborted') return;
      this.sttErrored = true;
      if (err === 'not-allowed') {
        this.toastr.error(this.translate.instant('aiVoiceInvoice.micDenied'));
      } else if (err !== 'no-speech') {
        this.toastr.error(this.translate.instant('aiVoiceInvoice.sttError'));
      }
    };

    rec.onend = () => {
      this.onSpeechRecognitionEnded();
    };

    try {
      rec.start();
      this.isRecording = true;
      this.cdr.markForCheck();
    } catch {
      this.speechRecognition = null;
      this.isRecording = false;
      this.toastr.error(this.translate.instant('aiVoiceInvoice.sttError'));
      this.cdr.markForCheck();
    }
  }

  private onSpeechRecognitionEnded(): void {
    if (this.sttIgnoreOnEnd) {
      this.sttIgnoreOnEnd = false;
      this.speechRecognition = null;
      this.isRecording = false;
      this.sttFinalAccum = '';
      this.cdr.markForCheck();
      return;
    }
    const hadError = this.sttErrored;
    this.sttErrored = false;
    const text = (this.draftText || '').trim();
    this.speechRecognition = null;
    this.isRecording = false;
    this.sttFinalAccum = '';
    this.cdr.markForCheck();
    if (text) {
      this.sendTextMessage();
    } else if (!hadError) {
      this.toastr.warning(this.translate.instant('aiVoiceInvoice.emptyRecording'));
    }
  }

  private abortSpeechRecognitionSilently(): void {
    if (!this.speechRecognition) return;
    this.sttIgnoreOnEnd = true;
    try {
      this.speechRecognition.abort();
    } catch {
      /* ignore */
    }
    this.speechRecognition = null;
    this.isRecording = false;
    this.sttFinalAccum = '';
  }

  private stopRecording(): void {
    if (!this.speechRecognition) {
      this.isRecording = false;
      return;
    }
    try {
      this.speechRecognition.stop();
    } catch {
      this.onSpeechRecognitionEnded();
    }
  }

  private afterVoiceStatus(status: AiVoiceStatus): void {
    if (status === 'error') {
      this.toastr.error(
        this.lastResponse?.message || this.translate.instant('aiVoiceInvoice.statusError'),
      );
    }
  }

  private appendVoiceTurnFromResponse(
    res: AiVoiceResponseDto,
    opts?: { linkTurnId?: string; omitMerchantBubble?: boolean },
  ): void {
    this.pendingMultiOptionIds.clear();
    const turnId = opts?.linkTurnId ?? this.newChatTurnId();
    const ts = Date.now();
    const voiceSnapshot = {
      status: res.status,
      message: res.message,
      missing: res.missing,
      options: res.options ? [...res.options] : undefined,
      multi_select: res.multi_select,
      confidence: res.confidence,
      resolved_customer_id: res.resolved_customer_id,
      resolved_customer_name: res.resolved_customer_name,
      invoice_result: res.invoice_result,
      can_confirm: res.can_confirm,
      can_save_draft: res.can_save_draft,
      open_modal_create_customer: res.open_modal_create_customer,
      open_modal_create_product: res.open_modal_create_product,
      suggested_customer_name: res.suggested_customer_name,
      suggested_product_name: res.suggested_product_name,
      detected_invoice_type_code: res.detected_invoice_type_code,
      detected_invoice_type_name: res.detected_invoice_type_name,
      suggested_customer_details: res.suggested_customer_details,
      suggested_product_details: res.suggested_product_details,
      report_html: res.report_html,
      report_data: res.report_data,
    };

    this.trackRepeatedAssistantPrompt(res.message);
    if (
      this.identicalAssistantPromptCount >= 2 &&
      this.isCustomerAskPrompt(res.message) &&
      !voiceSnapshot.open_modal_create_customer
    ) {
      voiceSnapshot.open_modal_create_customer = true;
      if (!voiceSnapshot.suggested_customer_name) {
        voiceSnapshot.suggested_customer_name =
          this.lastMerchantPlainText()?.trim() || undefined;
      }
    }

    if (!opts?.omitMerchantBubble && res.transcript?.trim()) {
      this.chatMessages.push({
        id: `merchant-${turnId}`,
        role: 'merchant',
        turnId,
        createdAt: ts,
        text: res.transcript.trim(),
      });
    }

    this.chatMessages.push({
      id: `system-${turnId}`,
      role: 'system',
      turnId,
      createdAt: ts,
      text: res.message,
      voiceResponse: voiceSnapshot,
      reportSafeHtml: res.report_html
        ? this.sanitizer.bypassSecurityTrustHtml(res.report_html)
        : undefined,
    });

    /* When invoice_result is present but invoice_id is null/absent, the backend signals
       the invoice was deleted — dismiss the preview immediately. */
    if (res.invoice_result) {
      const invId = res.invoice_result.invoice_id;
      if (invId != null && Number.isFinite(Number(invId))) {
        this.setPreviewInvoiceId(Number(invId));
      } else if (invId == null && res.invoice_result.error == null) {
        /* Explicit null/undefined invoice_id without an error = invoice deleted. */
        this.setPreviewInvoiceId(null);
        this.sessionLinkedInvoiceId = null;
      }
    }

    this.maybeOpenAssistantModals(res);
    this.maybeEnrichCustomerOptionsLocally(voiceSnapshot, turnId);

    this.scrollChatToBottom();
  }

  private trackRepeatedAssistantPrompt(message: string | undefined): void {
    const norm = this.normalizeSearchText(message || '');
    if (!norm) {
      this.lastAssistantPromptNorm = '';
      this.identicalAssistantPromptCount = 0;
      return;
    }
    if (norm === this.lastAssistantPromptNorm) {
      this.identicalAssistantPromptCount += 1;
    } else {
      this.lastAssistantPromptNorm = norm;
      this.identicalAssistantPromptCount = 1;
    }
  }

  private isCustomerAskPrompt(message: string | undefined): boolean {
    const t = message || '';
    return /اذكر اسم العميل|mention.*customer|customer name|ציין את שם הלקוח|שם הלקוח/i.test(t);
  }

  private lastMerchantPlainText(): string | null {
    for (let i = this.chatMessages.length - 1; i >= 0; i--) {
      const m = this.chatMessages[i];
      if (m.role !== 'merchant') continue;
      if (m.merchantListSelectionOnly || m.merchantImageUpload) continue;
      const text = (m.text || '').trim();
      if (text) return text;
    }
    return null;
  }

  private normalizeSearchText(value: string): string {
    return (value || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u064B-\u065F]/g, '')
      .replace(/[أإآٱ]/g, 'ا')
      .replace(/ة/g, 'ه')
      .replace(/ى/g, 'ي')
      .replace(/[^\p{L}\p{N}\s]/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private customerDisplayName(c: any): string {
    if (!c) return '';
    const lang = (this.translate.currentLang || this.curentLang || 'ar').toLowerCase();
    if (lang.startsWith('en')) {
      return c.customer_name_en || c.customer_name_ar || c.customer_name_he || c.customer_name || '';
    }
    if (lang.startsWith('he')) {
      return c.customer_name_he || c.customer_name_ar || c.customer_name_en || c.customer_name || '';
    }
    return c.customer_name_ar || c.customer_name_en || c.customer_name_he || c.customer_name || '';
  }

  private scoreCustomerNameMatch(query: string, customer: any): number {
    const q = this.normalizeSearchText(query);
    if (!q || q.length < 2) return 0;
    const names = [
      customer?.customer_name_ar,
      customer?.customer_name_en,
      customer?.customer_name_he,
      customer?.customer_name,
    ]
      .map((n) => this.normalizeSearchText(String(n || '')))
      .filter(Boolean);
    let best = 0;
    for (const name of names) {
      if (name === q) best = Math.max(best, 100);
      else if (name.includes(q) || q.includes(name)) best = Math.max(best, 85);
      else {
        const qTokens = q.split(' ').filter((t) => t.length > 1);
        const nTokens = new Set(name.split(' ').filter((t) => t.length > 1));
        if (!qTokens.length || !nTokens.size) continue;
        const hit = qTokens.filter((t) => nTokens.has(t) || [...nTokens].some((n) => n.includes(t) || t.includes(n)));
        const ratio = hit.length / qTokens.length;
        if (ratio >= 0.6) best = Math.max(best, Math.round(60 + ratio * 30));
      }
    }
    return best;
  }

  private ensureCompanyCustomersLoaded(done: (customers: any[]) => void): void {
    const companyId = Number(this.selectedCompany?.value) || 0;
    if (!companyId) {
      done([]);
      return;
    }
    if (
      this.companyCustomersCacheCompanyId === companyId &&
      this.companyCustomersCache.length
    ) {
      done(this.companyCustomersCache);
      return;
    }
    this.httpHelp.getCustomersByCompanyId(companyId, 5000, 0).subscribe({
      next: (res) => {
        const list = Array.isArray(res) ? res : [];
        this.companyCustomersCache = list;
        this.companyCustomersCacheCompanyId = companyId;
        done(list);
      },
      error: () => done([]),
    });
  }

  /**
   * AIBOT-3: when the assistant keeps asking for a customer without options,
   * match the last merchant text against company customers and offer chips.
   */
  private maybeEnrichCustomerOptionsLocally(
    voiceSnapshot: NonNullable<VoiceChatMessage['voiceResponse']>,
    turnId: string,
  ): void {
    if (this.sessionMode === 'report') return;
    if (voiceSnapshot.resolved_customer_id != null) return;
    if (voiceSnapshot.options?.length) return;

    const missing = voiceSnapshot.missing ?? [];
    const asksCustomer =
      missing.includes('customer') || this.isCustomerAskPrompt(voiceSnapshot.message);
    if (!asksCustomer) return;

    const query =
      this.lastMerchantPlainText() ||
      voiceSnapshot.suggested_customer_name?.trim() ||
      '';
    if (!query || query.length < 2) {
      if (this.identicalAssistantPromptCount >= 2) {
        voiceSnapshot.open_modal_create_customer = true;
        this.cdr.markForCheck();
      }
      return;
    }

    this.ensureCompanyCustomersLoaded((customers) => {
      const scored = customers
        .map((c) => ({
          c,
          score: this.scoreCustomerNameMatch(query, c),
          id: Number(c?.customer_id),
        }))
        .filter((x) => x.score >= 60 && Number.isFinite(x.id) && x.id > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 8);

      const systemMsg = this.chatMessages.find((m) => m.id === `system-${turnId}`);
      if (!systemMsg?.voiceResponse) return;

      if (scored.length === 1 && scored[0].score >= 85) {
        const only = scored[0];
        const label = this.customerDisplayName(only.c) || query;
        systemMsg.voiceResponse.options = [
          { id: only.id, label, kind: 'customer', score: only.score },
        ];
        systemMsg.voiceResponse.status = 'need_selection';
        systemMsg.voiceResponse.open_modal_create_customer = false;
      } else if (scored.length > 1) {
        systemMsg.voiceResponse.options = scored.map((x) => ({
          id: x.id,
          label: this.customerDisplayName(x.c) || String(x.id),
          kind: 'customer' as const,
          score: x.score,
        }));
        systemMsg.voiceResponse.status = 'need_selection';
        systemMsg.voiceResponse.open_modal_create_customer = true;
        systemMsg.voiceResponse.suggested_customer_name =
          systemMsg.voiceResponse.suggested_customer_name || query;
      } else {
        systemMsg.voiceResponse.open_modal_create_customer = true;
        systemMsg.voiceResponse.suggested_customer_name =
          systemMsg.voiceResponse.suggested_customer_name || query;
        if (!/لم يتم العثور|not found|לא נמצא/i.test(systemMsg.text || '')) {
          systemMsg.text = this.translate.instant('aiVoiceInvoice.customerNotFoundLocal', {
            name: query,
          });
          systemMsg.voiceResponse.message = systemMsg.text;
        }
      }
      this.cdr.markForCheck();
      this.scrollChatToBottom();
    });
  }

  /**
   * Live POST /text or /image — opens CRM/catalog modals when the assistant asks for
   * create-customer/product, and notifies when a detected document type differs from the session.
   * History replay via GET …/chat does not call this.
   */
  private maybeOpenAssistantModals(res: AiVoiceResponseDto): void {
    /* Detected invoice type from image — update session type and notify user. */
    if (
      res.detected_invoice_type_code != null &&
      Number.isFinite(res.detected_invoice_type_code) &&
      res.detected_invoice_type_code !== this.invoiceType
    ) {
      this.invoiceType = res.detected_invoice_type_code;
      const typeName =
        res.detected_invoice_type_name?.trim() ||
        String(res.detected_invoice_type_code);
      this.toastr.info(
        this.translate.instant('aiVoiceInvoice.detectedInvoiceTypeUpdated', { name: typeName }),
      );
    }

    /* Create-customer/product modals open from quick-reply buttons instead of auto-popup. */
  }

  /** Company id for add-customer modal (session setup). */
  get addModalCompanyId(): number | null {
    const v = this.selectedCompany?.value;
    if (v == null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }

  onAddCustomerModalClose(): void {
    this.showAddCustomerModal = false;
    this.pendingSuggestedCustomerName = null;
    this.pendingSuggestedCustomerDetails = null;
  }

  onCustomerCreatedFromAiModal(customer: Customer): void {
    this.showAddCustomerModal = false;
    this.pendingSuggestedCustomerName = null;
    this.pendingSuggestedCustomerDetails = null;
    this.cdr.markForCheck();

    const customerId = customer?.customer_id;
    if (customerId && customerId > 0 && this.sessionId && this.canSendMessages) {
      this.resolveEntityInSession({ kind: 'customer', entity_id: customerId });
    } else {
      const name =
        customer?.customer_name_ar ||
        customer?.customer_name_en ||
        customer?.customer_name_he ||
        '';
      if (name) {
        this.toastr.success(
          this.translate.instant('aiVoiceInvoice.customerCreatedContinue', { name }),
        );
      }
    }
  }

  onAddProductDialogHide(): void {
    this.showAddProductModal = false;
    this.pendingSuggestedProductName = null;
    this.pendingSuggestedProductDetails = null;
  }

  onProductCreatedFromAiModal(product: { product_id?: number; [key: string]: unknown }): void {
    this.showAddProductModal = false;
    this.pendingSuggestedProductName = null;
    this.pendingSuggestedProductDetails = null;
    this.cdr.markForCheck();

    const productId = product?.product_id;
    if (productId && productId > 0 && this.sessionId && this.canSendMessages) {
      this.resolveEntityInSession({ kind: 'product', entity_id: productId, quantity: 1 });
    } else {
      this.toastr.success(this.translate.instant('aiVoiceInvoice.productCreatedContinue'));
    }
  }

  /**
   * Links a newly-created customer or product to the active session and resumes the flow.
   * Called immediately after the modal emits a saved entity.
   */
  private resolveEntityInSession(body: PostAiResolveEntityDto): void {
    if (!this.sessionId) return;
    const turnId = this.newChatTurnId();
    this.isAiProcessing = true;
    this.lastResponse = null;
    this.cdr.markForCheck();
    this.scrollChatToBottom();

    this.aiInvoiceVoice.resolveEntity(this.sessionId, body).subscribe({
      next: (res) => {
        this.isAiProcessing = false;
        this.lastResponse = res;
        this.appendVoiceTurnFromResponse(res, { linkTurnId: turnId, omitMerchantBubble: false });
        this.afterVoiceStatus(res.status);
        this.refreshSessions();
        this.refreshSessionLinkedInvoice();
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.isAiProcessing = false;
        if (err.status === 404) {
          this.toastr.error(this.translate.instant('aiVoiceInvoice.sessionNotFound'));
          this.endSession();
        } else {
          this.toastr.error(this.httpErrorMessage(err, 'aiVoiceInvoice.resolveEntityError'));
        }
        this.cdr.markForCheck();
      },
    });
  }

  /* ================================================================
     Image upload — POST /session/:id/image
     ================================================================ */

  private readonly ACCEPTED_IMAGE_TYPES = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
    'application/pdf',
  ];
  private readonly MAX_IMAGE_BYTES = 10 * 1024 * 1024; // 10 MB

  get acceptedImageTypes(): string {
    return this.ACCEPTED_IMAGE_TYPES.join(',');
  }

  /** Open the native file picker — works even before a session exists. */
  triggerImageUpload(): void {
    if (this.hasSession && !this.canSendMessages) {
      this.toastr.warning(this.translate.instant('aiVoiceInvoice.readOnlySession'));
      return;
    }
    if (this.isAiProcessing || this.isUploadingImage) return;
    this.imageInput?.nativeElement.click();
  }

  /** Called by the hidden `<input type="file">` on change. */
  onImageFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    input.value = '';
    if (!file) return;

    if (!this.ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      this.toastr.error(this.translate.instant('aiVoiceInvoice.imageTypeNotSupported'));
      return;
    }
    if (file.size > this.MAX_IMAGE_BYTES) {
      this.toastr.error(this.translate.instant('aiVoiceInvoice.imageTooLarge'));
      return;
    }

    if (!this.sessionId) {
      /* No session yet — add merchant bubble, store file, kick off onboarding. */
      if (!this.bootstrapLoaded) {
        this.toastr.info(this.translate.instant('aiVoiceInvoice.loading'));
        return;
      }
      if (this.isAiProcessing) return;
      if (this.onboardingStep === 'company') {
        this.pendingImageFile = file;
        this.pendingDraft = '';
        const turnId = this.newChatTurnId();
        this.chatMessages.push({
          id: `merchant-${turnId}`,
          role: 'merchant',
          turnId,
          createdAt: Date.now(),
          text: file.name,
          merchantImageUpload: true,
          uploadedFileName: file.name,
        });
        this.scrollChatToBottom();
        this.cdr.markForCheck();
        return;
      }
      const turnId = this.newChatTurnId();
      this.chatMessages.push({
        id: `merchant-${turnId}`,
        role: 'merchant',
        turnId,
        createdAt: Date.now(),
        text: file.name,
        merchantImageUpload: true,
        uploadedFileName: file.name,
      });
      this.pendingImageFile = file;
      this.pendingDraft = '';
      this.kickOffOnboarding('');
      return;
    }

    this.uploadImageFile(file);
  }

  private uploadImageFile(file: File): void {
    if (!this.sessionId || !this.canSendMessages) return;

    const turnId = this.newChatTurnId();
    const ts = Date.now();
    const comment = this.draftText?.trim() || undefined;
    if (comment) this.draftText = '';

    /* Pre-add the merchant bubble immediately. */
    this.chatMessages.push({
      id: `merchant-${turnId}`,
      role: 'merchant',
      turnId,
      createdAt: ts,
      text: file.name,
      merchantImageUpload: true,
      uploadedFileName: file.name,
    });

    this.isUploadingImage = true;
    this.isAiProcessing = true;
    this.lastResponse = null;
    this.pendingMultiOptionIds.clear();
    this.cdr.markForCheck();
    this.scrollChatToBottom();

    this.dispatchImageToApi(file, turnId, comment);
  }

  /**
   * Send the image to the API without adding a merchant bubble.
   * Used both after the regular upload (bubble already added) and after onboarding completes.
   */
  private dispatchImageToApi(file: File, linkTurnId: string, comment?: string): void {
    if (!this.sessionId) {
      this.isUploadingImage = false;
      this.isAiProcessing = false;
      this.cdr.markForCheck();
      return;
    }
    this.aiInvoiceVoice.postImage(this.sessionId, file, comment).subscribe({
      next: (res) => {
        this.isUploadingImage = false;
        this.isAiProcessing = false;
        this.lastResponse = res;
        this.appendVoiceTurnFromResponse(res, { linkTurnId, omitMerchantBubble: true });
        this.afterVoiceStatus(res.status);
        this.refreshSessions();
        this.refreshSessionLinkedInvoice();
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.isUploadingImage = false;
        this.isAiProcessing = false;
        if (err.status === 404) {
          this.toastr.error(this.translate.instant('aiVoiceInvoice.sessionNotFound'));
          this.endSession();
        } else if (err.status === 400) {
          this.toastr.error(this.httpErrorMessage(err, 'aiVoiceInvoice.imageUploadBadRequest'));
        } else {
          this.toastr.error(this.httpErrorMessage(err, 'aiVoiceInvoice.imageUploadError'));
        }
        this.cdr.markForCheck();
      },
    });
  }

  private scrollChatToBottom(): void {
    setTimeout(() => {
      const el = this.chatScroll?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    }, 80);
  }

  /** Shown under system bubble when backend locks a customer (optional name + id). */
  customerResolvedLine(m: VoiceChatMessage): string {
    const vr = m.voiceResponse;
    if (!vr) return '';
    const id = vr.resolved_customer_id;
    const name = vr.resolved_customer_name?.trim();
    if (name && id != null) {
      return this.translate.instant('aiVoiceInvoice.customerResolvedWithName', { id, name });
    }
    if (id != null) {
      return this.translate.instant('aiVoiceInvoice.customerResolved', { id });
    }
    if (name) {
      return this.translate.instant('aiVoiceInvoice.customerResolvedNameOnly', { name });
    }
    return '';
  }

  /**
   * Backend may still list `customer` in `missing` while `resolved_customer_id` is set —
   * hide redundant chip so the UI matches session state.
   */
  missingChipsForDisplay(m: VoiceChatMessage): string[] {
    const miss = m.voiceResponse?.missing;
    if (!miss?.length) return [];
    const vr = m.voiceResponse;
    if (vr?.resolved_customer_id != null && miss.includes('customer')) {
      return miss.filter((x) => x !== 'customer');
    }
    return miss;
  }

  private latestSummaryMessage(): VoiceChatMessage | null {
    for (let i = this.chatMessages.length - 1; i >= 0; i--) {
      const m = this.chatMessages[i];
      if (
        m.role === 'system' &&
        !m.isConfirmSuccess &&
        !m.setupStep &&
        !m.reportSafeHtml &&
        m.voiceResponse
      ) {
        return m;
      }
    }
    return null;
  }

  canShowUnderstoodSummary(m: VoiceChatMessage): boolean {
    if (this.sessionMode === 'report') return false;
    if (!m.voiceResponse || m.setupStep || m.isConfirmSuccess || m.reportSafeHtml) return false;
    if (m !== this.latestSummaryMessage()) return false;
    const st = m.voiceResponse.status;
    return st === 'missing_data' || st === 'success' || st === 'need_selection';
  }

  missingFieldLabel(key: string): string {
    const map: Record<string, string> = {
      customer: 'aiVoiceInvoice.summaryCustomer',
      amount: 'aiVoiceInvoice.summaryAmount',
      items: 'aiVoiceInvoice.summaryDescription',
      invoice_lines: 'aiVoiceInvoice.summaryDescription',
      product: 'aiVoiceInvoice.summaryDescription',
      due: 'aiVoiceInvoice.summaryDueDate',
      payment: 'aiVoiceInvoice.summaryPaymentTerms',
      vat: 'aiVoiceInvoice.summaryTax',
      tax: 'aiVoiceInvoice.summaryTax',
    };
    for (const [needle, i18nKey] of Object.entries(map)) {
      if (key.includes(needle)) return this.translate.instant(i18nKey);
    }
    return key;
  }

  optionKindLabel(kind: string | undefined): string {
    if (kind === 'customer') return this.translate.instant('aiVoiceInvoice.optionKindCustomer');
    if (kind === 'product') return this.translate.instant('aiVoiceInvoice.optionKindProduct');
    return kind || '';
  }

  buildUnderstoodSummary(m: VoiceChatMessage): UnderstoodSummaryRow[] {
    const vr = m.voiceResponse!;
    const missing = new Set(this.missingChipsForDisplay(m));
    const snap = this.sessionDraftSnapshot;
    const lines = (snap?.['resolvedLines'] as Array<Record<string, unknown>>) ?? [];
    const firstLine = lines[0];
    const payment = (snap?.['payment'] as Record<string, unknown>) ?? {};
    const itemsTotal = Number(payment['items_total']);
    const paymentMethods = Array.isArray(payment['payment_methods'])
      ? (payment['payment_methods'] as Array<Record<string, unknown>>)
      : [];
    const needsConfirm = this.translate.instant('aiVoiceInvoice.summaryNeedsConfirmation');
    const needsSelection = this.translate.instant('aiVoiceInvoice.summaryNeedsSelection');
    const notFilled = this.translate.instant('aiVoiceInvoice.summaryNotFilled');

    const docType =
      vr.detected_invoice_type_name?.trim() ||
      this.typeLabel ||
      '';

    const resolvedCustomer = vr.resolved_customer_name?.trim() || '';
    const suggestedCustomer = vr.suggested_customer_name?.trim() || '';
    const customerName = resolvedCustomer || suggestedCustomer;
    const customerStatus: UnderstoodSummaryRow['status'] = resolvedCustomer
      ? 'ok'
      : suggestedCustomer
        ? 'warn'
        : 'missing';

    const description = String(firstLine?.['item_name'] ?? '').trim();
    const descriptionStatus: UnderstoodSummaryRow['status'] = description
      ? 'ok'
      : 'missing';

    let amountText = '';
    if (Number.isFinite(itemsTotal) && itemsTotal > 0) {
      amountText = `${itemsTotal.toLocaleString()} ${this.translate.instant('aiVoiceInvoice.currencyShekel')}`;
    }
    const amountStatus: UnderstoodSummaryRow['status'] = amountText ? 'ok' : 'missing';

    const lineVat = Number(firstLine?.['vat_amount']);
    const taxMissing = [...missing].some(
      (k) => k.includes('vat') || k.includes('tax') || k.includes('isVat'),
    );
    let taxValue = notFilled;
    let taxStatus: UnderstoodSummaryRow['status'] = 'missing';
    if (taxMissing) {
      taxValue = needsConfirm;
      taxStatus = 'warn';
    } else if (Number.isFinite(lineVat) && lineVat > 0) {
      taxValue = `${lineVat.toLocaleString()} ${this.translate.instant('aiVoiceInvoice.currencyShekel')}`;
      taxStatus = 'ok';
    } else if (amountText) {
      taxValue = this.translate.instant('aiVoiceInvoice.summaryTaxConfirmed');
      taxStatus = 'ok';
    }

    const dueMissing = [...missing].some(
      (k) => k.includes('due') || k === 'date' || k.includes('due_date'),
    );
    let paymentTerms = '';
    if (paymentMethods.length) {
      paymentTerms = paymentMethods
        .map((pm) => String(pm['type'] || '').trim())
        .filter(Boolean)
        .join(', ');
    }
    const dueStatus: UnderstoodSummaryRow['status'] = dueMissing
      ? 'missing'
      : paymentTerms
        ? 'ok'
        : 'warn';
    const dueValue = dueMissing
      ? needsSelection
      : paymentTerms || this.translate.instant('aiVoiceInvoice.summaryDueDefault');

    const invoiceDateRaw =
      (snap?.['invoice_date'] as string) ||
      (snap?.['invoiceDate'] as string) ||
      '';
    const invoiceDate = invoiceDateRaw
      ? String(invoiceDateRaw).slice(0, 10)
      : '';

    return [
      {
        id: 'docType',
        labelKey: 'aiVoiceInvoice.summaryDocType',
        value: docType || notFilled,
        status: docType ? 'ok' : 'missing',
        icon: 'fa-file-text-o',
      },
      {
        id: 'customer',
        labelKey: 'aiVoiceInvoice.summaryCustomer',
        value: customerName || notFilled,
        status: customerStatus,
        icon: 'fa-user',
      },
      {
        id: 'description',
        labelKey: 'aiVoiceInvoice.summaryDescription',
        value: description || notFilled,
        status: descriptionStatus,
        icon: 'fa-tag',
      },
      {
        id: 'amount',
        labelKey: 'aiVoiceInvoice.summaryAmount',
        value: amountText || notFilled,
        status: amountStatus,
        icon: 'fa-money',
      },
      {
        id: 'tax',
        labelKey: 'aiVoiceInvoice.summaryTax',
        value: taxValue,
        status: taxStatus,
        icon: 'fa-percent',
      },
      {
        id: 'invoiceDate',
        labelKey: 'aiVoiceInvoice.summaryInvoiceDate',
        value: invoiceDate || notFilled,
        status: invoiceDate ? 'ok' : 'warn',
        icon: 'fa-calendar-o',
      },
      {
        id: 'dueDate',
        labelKey: 'aiVoiceInvoice.summaryPaymentTerms',
        value: dueValue,
        status: dueStatus,
        icon: 'fa-calendar',
      },
    ];
  }

  bubbleClassForStatus(status: AiVoiceStatus): string {
    switch (status) {
      case 'success':
        return 'bubble--success';
      case 'missing_data':
      case 'need_selection':
        return 'bubble--warn';
      case 'error':
        return 'bubble--error';
      default:
        return '';
    }
  }

  /** Session/timer guards for confirm/save UI (no hide from issued-invoice yet — flags may override). */
  private confirmActionsGuardsOk(): boolean {
    return !!(
      this.sessionId &&
      this.canSendMessages &&
      this.confirmResult == null &&
      !this.isConfirming
    );
  }

  /** Backend sent explicit button flags on this message's snapshot. */
  private messageUsesBackendConfirmFlags(m: VoiceChatMessage): boolean {
    const vr = m.voiceResponse;
    return vr?.can_confirm !== undefined || vr?.can_save_draft !== undefined;
  }

  /**
   * Wrapper for confirm/save row: backend `can_confirm` / `can_save_draft` when present;
   * otherwise legacy rule (last `success` turn + no issued invoice yet).
   */
  canShowConfirmActionsOnMessage(m: VoiceChatMessage): boolean {
    if (!this.confirmActionsGuardsOk()) return false;
    if (m.role !== 'system' || m.isConfirmSuccess || !m.voiceResponse) return false;

    if (this.messageUsesBackendConfirmFlags(m)) {
      return m.voiceResponse.can_confirm === true || m.voiceResponse.can_save_draft === true;
    }
    return this.canShowConfirmActionsLegacy(m);
  }

  /** Show «Confirm / Issue» button. */
  canShowConfirmSubmitButton(m: VoiceChatMessage): boolean {
    if (!this.confirmActionsGuardsOk()) return false;
    if (m.role !== 'system' || m.isConfirmSuccess || !m.voiceResponse) return false;

    if (this.messageUsesBackendConfirmFlags(m)) {
      return m.voiceResponse.can_confirm === true;
    }
    return this.canShowConfirmActionsLegacy(m);
  }

  /** Show «Save as draft» button. */
  canShowSaveDraftButton(m: VoiceChatMessage): boolean {
    if (!this.confirmActionsGuardsOk()) return false;
    if (m.role !== 'system' || m.isConfirmSuccess || !m.voiceResponse) return false;

    if (this.messageUsesBackendConfirmFlags(m)) {
      return m.voiceResponse.can_save_draft === true;
    }
    return this.canShowConfirmActionsLegacy(m);
  }

  /**
   * Legacy: last system message with `success`, manual confirm allowed (draft not yet issued as invoice row).
   */
  private canShowConfirmActionsLegacy(m: VoiceChatMessage): boolean {
    if (this.shouldHideManualConfirm()) return false;
    if (!this.lastResponse || this.lastResponse.status !== 'success') return false;
    if (m.voiceResponse?.status !== 'success') return false;
    const i = this.lastSuccessDraftMessageIndex();
    if (i < 0) return false;
    return this.chatMessages[i] === m;
  }

  private lastSuccessDraftMessageIndex(): number {
    for (let j = this.chatMessages.length - 1; j >= 0; j--) {
      const row = this.chatMessages[j];
      if (
        row.role === 'system' &&
        !row.isConfirmSuccess &&
        row.voiceResponse?.status === 'success'
      ) {
        return j;
      }
    }
    return -1;
  }

  confirmInvoice(confirm: boolean): void {
    if (!this.sessionId || !this.canSendMessages) {
      this.toastr.warning(
        this.canSendMessages
          ? this.translate.instant('aiVoiceInvoice.needSession')
          : this.translate.instant('aiVoiceInvoice.readOnlySession'),
      );
      return;
    }

    const lr = this.lastResponse;
    const useBackendFlags =
      lr?.can_confirm !== undefined || lr?.can_save_draft !== undefined;

    if (useBackendFlags) {
      if (confirm && !lr?.can_confirm) {
        this.toastr.warning(this.translate.instant('aiVoiceInvoice.confirmNotAllowed'));
        return;
      }
      if (!confirm && !lr?.can_save_draft) {
        this.toastr.warning(this.translate.instant('aiVoiceInvoice.saveDraftNotAllowed'));
        return;
      }
    } else {
      if (this.shouldHideManualConfirm()) {
        this.toastr.info(this.translate.instant('aiVoiceInvoice.confirmAlreadyIssued'));
        return;
      }
      if (lr?.status !== 'success') {
        this.toastr.warning(this.translate.instant('aiVoiceInvoice.confirmOnlyAfterSuccess'));
        return;
      }
    }
    this.isConfirming = true;
    this.spinner.show();
    this.aiInvoiceVoice.confirm(this.sessionId, { confirm }).subscribe({
      next: (res) => {
        this.isConfirming = false;
        this.spinner.hide();
        this.confirmResult = res;
        if (res.invoice_id != null && Number.isFinite(Number(res.invoice_id))) {
          this.setPreviewInvoiceId(Number(res.invoice_id));
        }
        const msg =
          this.pickLocalizedMessage(res) ||
          res.message ||
          this.translate.instant('aiVoiceInvoice.confirmOk');
        if (res.success === false) {
          this.toastr.error(msg);
        } else {
          this.toastr.success(msg);
          this.appendConfirmSuccessMessage(res);
          this.invalidateLocalSessionKeepResult();
        }
        this.clearPersistedSession();
        this.refreshSessions();
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.isConfirming = false;
        this.spinner.hide();
        if (err.status === 404) {
          this.toastr.error(this.translate.instant('aiVoiceInvoice.sessionNotFound'));
          this.endSession();
        } else {
          this.toastr.error(this.httpErrorMessage(err, 'aiVoiceInvoice.confirmError'));
        }
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Open invoice in app (invoices-v2). Pass `invoiceId` from chat (`invoice_result` / post-confirm line)
   * or omit to use `confirmResult` from the success panel.
   */
  goToInvoiceView(invoiceId?: number | null): void {
    const id = invoiceId ?? this.confirmResult?.invoice_id;
    if (id != null) {
      this.router.navigate(['/dashboard/invoices-v2/view'], { queryParams: { invoiceId: id } });
    } else {
      this.router.navigate(['/dashboard/invoices-v2']);
    }
  }

  resetAfterSuccess(): void {
    this.endSession();
  }

  /** Download current chat as `.txt` (readable) or `.json` (full structure). */
  exportChat(format: 'text' | 'json'): void {
    if (!this.chatMessages.length) {
      this.toastr.warning(this.translate.instant('aiVoiceInvoice.exportEmpty'));
      return;
    }
    const sid = (this.sessionId || 'session').replace(/[^\w-]+/g, '_').slice(0, 40);
    if (format === 'json') {
      const body = {
        sessionId: this.sessionId,
        exportedAt: new Date().toISOString(),
        messages: this.chatMessages.map((m) => ({
          role: m.role,
          text: m.text,
          createdAt: new Date(m.createdAt).toISOString(),
          merchantListSelectionOnly: m.merchantListSelectionOnly,
          voiceResponse: m.voiceResponse,
          isConfirmSuccess: m.isConfirmSuccess,
        })),
      };
      this.downloadTextFile(
        JSON.stringify(body, null, 2),
        `ai-invoice-chat-${sid}.json`,
        'application/json;charset=utf-8',
      );
    } else {
      const merchantLabel = this.translate.instant('aiVoiceInvoice.exportRoleMerchant');
      const systemLabel = this.translate.instant('aiVoiceInvoice.assistantName');
      const lines: string[] = [
        `${this.translate.instant('aiVoiceInvoice.sessionId')}: ${this.sessionId || '—'}`,
        `${this.translate.instant('aiVoiceInvoice.exportedAt')}: ${new Date().toLocaleString()}`,
        '',
      ];
      for (const m of this.chatMessages) {
        const who = m.role === 'merchant' ? merchantLabel : systemLabel;
        lines.push(`[${who}] ${new Date(m.createdAt).toLocaleString()}`);
        lines.push(m.text);
        lines.push('');
      }
      this.downloadTextFile(lines.join('\n'), `ai-invoice-chat-${sid}.txt`, 'text/plain;charset=utf-8');
    }
    this.toastr.success(this.translate.instant('aiVoiceInvoice.exportDone'));
  }

  private downloadTextFile(content: string, filename: string, mime: string): void {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  sessionPreviewTitle(item: AiSessionListItemDto): string {
    if (this.curentLang === 'ar' && item.company_name_ar) return item.company_name_ar;
    if (this.curentLang === 'he' && item.company_name_he) return item.company_name_he;
    return item.company_name_en || item.company_name_ar || item.sessionId.slice(0, 8);
  }

  sessionInitials(item: AiSessionListItemDto): string {
    const raw = this.sessionPreviewTitle(item) || '#';
    const parts = raw.trim().split(/\s+/).slice(0, 2);
    const first = parts[0]?.[0] || '#';
    const second = parts[1]?.[0] || '';
    return (first + second).toUpperCase();
  }

  sessionRelativeTime(item: AiSessionListItemDto): string {
    const iso = item.updatedAt || item.createdAt;
    if (!iso) return '';
    const then = new Date(iso).getTime();
    if (!Number.isFinite(then)) return '';
    const diffMs = Date.now() - then;
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return this.translate.instant('aiVoiceInvoice.timeJustNow');
    const min = Math.floor(sec / 60);
    if (min < 60) return this.translate.instant('aiVoiceInvoice.timeMinAgo', { n: min });
    const hr = Math.floor(min / 60);
    if (hr < 24) return this.translate.instant('aiVoiceInvoice.timeHourAgo', { n: hr });
    const days = Math.floor(hr / 24);
    if (days < 30) return this.translate.instant('aiVoiceInvoice.timeDayAgo', { n: days });
    return new Date(iso).toLocaleDateString();
  }

  private appendConfirmSuccessMessage(res: AiInvoiceConfirmResponseDto): void {
    const parts: string[] = [];
    if (res.document_number) {
      parts.push(
        this.translate.instant('aiVoiceInvoice.chatInvoiceCreated', {
          number: res.document_number,
        }),
      );
    } else if (res.invoice_id != null) {
      parts.push(
        this.translate.instant('aiVoiceInvoice.chatInvoiceCreatedId', {
          id: res.invoice_id,
        }),
      );
    } else {
      parts.push(this.translate.instant('aiVoiceInvoice.confirmOk'));
    }
    this.chatMessages.push({
      id: `confirm-${Date.now()}`,
      role: 'system',
      turnId: 'confirm',
      createdAt: Date.now(),
      text: parts.join(' '),
      isConfirmSuccess: true,
      confirmedInvoiceId: res.invoice_id != null ? Number(res.invoice_id) : undefined,
    });
    this.scrollChatToBottom();
  }

  private pickLocalizedMessage(res: AiInvoiceConfirmResponseDto): string | null {
    if (this.curentLang === 'ar' && res.messageAr) return res.messageAr;
    if (this.curentLang === 'he' && res.messageHe) return res.messageHe;
    if (res.messageEn) return res.messageEn;
    return null;
  }

  private httpErrorMessage(err: HttpErrorResponse, fallbackKey: string): string {
    const e = err.error;
    if (typeof e === 'string' && e.trim()) return e;
    if (e && typeof e === 'object') {
      const msg =
        e.message ||
        e.messageAr ||
        e.messageEn ||
        e.messageHe ||
        (Array.isArray(e.message) ? e.message[0] : null);
      if (msg) return String(msg);
    }
    return this.translate.instant(fallbackKey);
  }
}
